/**
 * File operations API client — ID-based, tree-storage backed
 *
 * All operations use file IDs, not paths.
 * Listing is by parentId (null = root).
 */
import { request, fetchRaw, getApiBase, getDefaultHeaders, getActiveTenantId, getActiveOrgEmail, identityHeaders, isCurrentIdentity, recordActingIdentity, currentSessionSignal, dropIfSessionEnded, isSessionEndedError, sessionEndedSince, sessionFetch, type RequestIdentity, currentAccount, carryActingStamp } from '@papan/api-client'
import type { Trigger, Schedule, MiniappIconSpec, PartitionPreview } from '@papan/types'
import { extractJobFailureError, extractPartialOf, type ExtractFailureError } from './extract-failure'
export type { ExtractPartial } from './extract-failure'
import { IDEMPOTENCY_KEY_HEADER, MOVE_JOB_STATUS_UNKNOWN_CODE, isContentDamagedCode, damagedMessageFrom, stripPointerSuffix, parseContentDispositionFilename, toWireConflictStrategy } from '@papan/types'
import { uploadFileWithTus } from './upload-tus.js'
import { createJobTracker, JobFailedError, requestSignal, requestOutcomeUnknown, type JobStatus, type JobTrackerTimings } from './job-tracker.js'

/** E7 PR 0b (D134) — an undo's request acts as the identity of the session that STARTED it (`captureRequestIdentity`,
 *  pinned by the undo provider): a tenant / identity switch mid-undo never sends its follow-on requests as the new
 *  context. Never in a body. A GET keeps request()'s stall retry (an identity is a header, not a signal). */
export interface PinnedIdentityOptions { identity?: RequestIdentity }
/** `identity` only when one is pinned (request()'s option shape). */
const pinnedOpt = (opts?: PinnedIdentityOptions) => (opts?.identity ? { identity: opts.identity } : {})
import type { DeleteJobOutcome } from './delete-tracker.js'
import type { RestoreJobOutcome } from './restore-tracker.js'
import type { EmptyBinJobOutcome } from './empty-bin-tracker.js'
import type { BinPurgeStartResult, BinPurgeJobStatus } from './bin-purge-tracker.js'

/**
 * #762 — a client-minted key for one action, sent as `x-idempotency-key`. The server keys the action's
 * queued job on it, so the same request sent again (below, or by the browser) joins that job. Never
 * `x-operation-id`: that one turns on per-file progress events, which only a caller showing progress asks for.
 */
function mintIdempotencyKey(kind: string): string {
  const rnd = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `${kind}-${rnd}`
}

/**
 * #762 — send a keyed request, and ask ONCE more when its answer was lost (a dropped connection, our own timeout, a
 * gateway giving up while the origin carried on — `requestOutcomeUnknown`). The server may have acted on the first;
 * the repeat carries the SAME `x-idempotency-key`, so it joins the queued job (copy, cross-tenant copy, a queued
 * extract or cross-org move — operation-job-id.ts) or gets the first request's stored answer back (an inline delete,
 * move or extract — the server's replayable-response.ts). A real refusal is never repeated.
 */
/** A raw `fetch` that got a gateway's answer (502/503/504, Cloudflare's 52x) instead of the application's: the
 *  outcome is unknown, so say so the way `request` does (`requestOutcomeUnknown` reads the status). */
function throwIfGateway(res: Response): void {
  if (res.status === 502 || res.status === 503 || res.status === 504 || (res.status >= 520 && res.status <= 524)) {
    throw Object.assign(new Error(`The server did not answer (${res.status})`), { status: res.status })
  }
}

async function sendOnceMore<T>(send: () => Promise<T>): Promise<T> {
  try {
    return await send()
  } catch (err) {
    if (!requestOutcomeUnknown(err)) throw err
    return send()
  }
}

/**
 * The browser refused to open a save dialog — almost always because the click's
 * transient activation (~5s) was spent before we asked.
 *
 * THROWN rather than returned. A returned flag reached exactly one of the eight
 * call sites: the other seven wrap `download()` in `.catch()` or `try/catch`,
 * which a resolved value walks straight past, so each of them gained a silent
 * "click Download, nothing happens" mode the moment this stopped being
 * batch-only. Throwing means every existing handler surfaces it for free.
 *
 * `code` lets a caller localise; `message` is the fallback for the ones that
 * just print `e.message`.
 */
export class PickerBlockedError extends Error {
  readonly code = 'PICKER_BLOCKED' as const
  constructor() {
    super('The browser did not open the save dialog. Please choose Download again.')
    this.name = 'PickerBlockedError'
  }
}

/**
 * #1264 — an action on a LISTED row (move / rename, delete; the file viewer's "gone") refused with
 * 403 / 404: the row may be gone — a delete whose announcement was lost between its commit and its
 * publish (the owner chose no outbox: "loss is not lag") — or the caller may just not be allowed. The
 * server answers both alike on purpose (no existence oracle), so the client never guesses: the lists
 * that hold the row re-read its folder, which drops a gone row and changes nothing otherwise.
 *
 * A DEDICATED channel, never a file event: only the LIST holders subscribe (`FileListCacheSync`,
 * `useFiles`, the explorer trees) — a viewer, a tab or a detail query keyed on the row's id never
 * hears it, so nothing re-reads the refused row itself (which would refuse again, and loop). A row
 * reported again within a few seconds is not re-announced.
 */
type ListedRowRefusedHandler = (fileId: string) => void
const listedRowRefusedHandlers = new Set<ListedRowRefusedHandler>()
const lastRefusalReport = new Map<string, number>()
const REFUSAL_REPORT_DEDUPE_MS = 3_000
export function onListedRowRefused(handler: ListedRowRefusedHandler): () => void {
  listedRowRefusedHandlers.add(handler)
  return () => { listedRowRefusedHandlers.delete(handler) }
}
/** Report a listed row whose action was refused (or that a surface found dead itself). */
export function reportListedRowRefused(fileId: string): void {
  const now = Date.now()
  const last = lastRefusalReport.get(fileId)
  if (last !== undefined && now - last < REFUSAL_REPORT_DEDUPE_MS) return
  if (lastRefusalReport.size > 500) lastRefusalReport.clear()
  lastRefusalReport.set(fileId, now)
  for (const h of listedRowRefusedHandlers) {
    try { h(fileId) } catch { /* a refresh hint never fails the caller */ }
  }
}
function noteRowRefused(fileId: string | null | undefined, err: unknown): void {
  const status = (err as { status?: number } | null)?.status
  if (fileId && (status === 403 || status === 404)) reportListedRowRefused(fileId)
}
/** Run a per-row call; a 403 / 404 is reported (`noteRowRefused`) and rethrown unchanged. */
async function reportingRowRefusal<T>(fileId: string, run: () => Promise<T>): Promise<T> {
  try {
    return await run()
  } catch (err) {
    noteRowRefused(fileId, err)
    throw err
  }
}

/**
 * #801 — `moveFile`'s refusal path when the server's answer is none of
 * accessChange / crossTenant / chainEscape / nestedSharedBlocked: a protected-branch
 * chokepoint refusal (409 `PROTECTED_BRANCHES` — `entityRoot` / `needsManage` /
 * `wouldLoseAccess`, exactly as `apps/api/src/middleware/error.ts` puts them on the
 * wire; a whole-app/project root, a split needing Manage on the root, or a split that
 * would leave the MOVER unable to manage the new shell — that last one can only be
 * learned from the act itself, never the preview), 409 `PARTITION_CHANGED` (the
 * subtree changed since the preview — retryable), 409 `SUBTREE_TOO_DEEP`, or a 403
 * governance-judge refusal (`ShellGovernanceError`, whose wire `code` is the judge's
 * own code, not a fixed string). Carries `status` + whatever fields the body had, so a
 * caller (Task 12) branches on `.code`/`.entityRoot`/`.needsManage`/`.wouldLoseAccess`
 * instead of parsing `message` — the same field-attachment convention
 * `shared/api-client`'s `request()` uses for `deleteFile`'s refusals.
 */
export class MoveRefusedError extends Error {
  readonly status: number
  readonly code?: string
  readonly entityRoot?: boolean
  readonly needsManage?: boolean
  readonly wouldLoseAccess?: boolean
  /** A 409 name conflict in the destination (`{ conflict: true }`). */
  readonly conflict?: boolean
  /** #1397 K1: a `CONTENT_DAMAGED` refusal's damaged file, as the server named it (`name` on the wire). */
  readonly damagedName?: string
  constructor(message: string, opts: {
    status: number; code?: string; entityRoot?: boolean; needsManage?: boolean; wouldLoseAccess?: boolean; conflict?: boolean; damagedName?: string
  }) {
    super(message)
    this.name = 'MoveRefusedError'
    this.status = opts.status
    this.code = opts.code
    this.entityRoot = opts.entityRoot
    this.needsManage = opts.needsManage
    this.wouldLoseAccess = opts.wouldLoseAccess
    this.conflict = opts.conflict
    this.damagedName = opts.damagedName
  }
}

/** What `filesApi.moveFile` answers (a refusal throws — see `readMoveAnswer`). */
export interface MoveFileAnswer {
  success: boolean; file: FileListItem; conflict?: boolean; error?: string
  accessChange?: boolean; needsConfirmation?: boolean
  diff?: Array<{ targetType: string; targetId: string; fromPerm: string | null; toPerm: string | null }>
  /** #801: a SPLIT move — the root stayed in place; only these branches moved under a new
   *  destination shell (`file` above is that shell). A replay (`replay: true`) moved
   *  nothing and carries no `skipped`/counts. Absent entirely on a WHOLE move. */
  split?: {
    replay?: boolean
    destFolderId?: string
    moved: string[]
    /** What stayed behind, as the caller's own listing shows it. Absent on a replay. */
    skipped?: PartitionPreview
    /** What stayed with the root's own access setup (E2): its share links, org shares
     *  and app grants. Absent on a replay, or when nothing of the root's own access
     *  setup was carried (a cross-tenant mount split never reports these). */
    linksStayed?: number
    orgSharesStayed?: number
    appGrantsStayed?: number
  }
  /** The rename activated a `.chain` but its drafted target was invalid (another
   *  chain / non-folder / loop / no-read / unparseable). The rename SUCCEEDED;
   *  the target was NOT accepted (the chain keeps its previous target). The
   *  client toasts this as a warning. */
  chainTargetWarning?: string
  /** Reverse-share gesture (step 7): the destination is a cross-tenant
   *  `.shared` (or a `.chain` resolving cross-tenant). The move did NOT
   *  happen — `reverseShare` carries the preview the caller uses to open the
   *  Share dialog pre-targeted at the owner org. */
  crossTenant?: boolean
  reverseShare?: {
    sourceFileId: string; sourceName: string; sourceIsFolder: boolean
    recipientTenantId: string; recipientOrgName: string
  }
  /** Chain-escape gesture: moving a `.chain` (or a folder containing one)
   *  into an outbound-shared folder where the chain's target is OUTSIDE the
   *  shared subtree. The move did NOT happen — the caller opens the
   *  ChainEscapeDialog. Re-call with `confirmedChainEscape: true` to proceed. */
  chainEscape?: boolean
  escape?: {
    sharedFolderId: string; sharedFolderName: string
    recipients: Array<{ tenantId: string; orgName: string }>
    escapingChains: Array<{ chainId: string; chainName: string; targetId: string; targetName: string; targetIsFolder: boolean }>
  }
  /** Decision 9 / Phase 29: surfaced when active share-links point at the
   *  moved file. UI shows a non-blocking advisory toast. */
  shareLinkAdvisory?: { count: number }
  /** A legacy server resolved this "move" as a COPY (the source untouched): say "Copied", record
   *  no move-undo. A current server MOVES across organisations (`movedAcrossOrgs`). */
  copied?: boolean
  /** A move across organisations (owner, 2026-10-06): the item landed in the other drive
   *  (`newFileRef` deletes it there) and its source went to its owning tenant's Bin (`binId`). */
  movedAcrossOrgs?: { newFileRef: string; binId?: string; sourceTenantId: string }
  sourceUnchanged?: boolean
  /** #753 folder copy: the top-level items the queued copy created. */
  copiedItems?: Array<{ id: string; name: string }>
  /** #753 folder copy: per-item reasons when the copy was only partial. */
  copyErrors?: Array<{ sourceId: string; message: string }>
  /** #982: a folder moved out of another organisation's share kept this many of its parts where they were
   *  (the mover can't remove them — the folder stays there as a shell around them). */
  movedKept?: number
}

/**
 * The move's answer (status + JSON body), read ONE way whether it came inline or from a `file-move` job's stored
 * result (#1167 S4): the access-change / reverse-share / chain-escape answers are returned as data, a refusal throws
 * `MoveRefusedError` with every field, the nested-`.shared` block throws its `Error`, and #753 / #982's queued copy
 * or cross-org move is followed to its end.
 */
async function readMoveAnswer(status: number, data: any): Promise<MoveFileAnswer> {
  if (!(status >= 200 && status < 300)) {
    if (data.accessChange) {
      return data // Return the full response with diff for the caller to handle
    }
    if (data.crossTenant) {
      return data // Reverse-share gesture — caller opens the Share dialog
    }
    if (data.chainEscape) {
      return data // Chain-escape gesture — caller opens the ChainEscapeDialog
    }
    // Nested-`.shared` HARD block (one-hop invariant): no dialog, no bypass.
    // Throw a clear message so every move caller's try/catch surfaces it
    // uniformly (drag, paste, chain-escape re-calls) — never a silent no-op.
    if (data.nestedSharedBlocked && data.block) {
      const orgs: string = (data.block.recipients ?? [])
        .map((r: { orgName: string }) => r.orgName)
        .filter(Boolean)
        .join(', ')
      throw new Error(
        `Can’t move a shared item into “${data.block.sharedFolderName}”` +
        (orgs ? ` — that folder is shared with ${orgs}` : '') +
        `. A shared item lives in exactly one place; move it out of the shared folder first.`,
      )
    }
    // #801: PROTECTED_BRANCHES / PARTITION_CHANGED / SUBTREE_TOO_DEEP (409) and a
    // governance-judge refusal (403) all land here — surfaced as a typed error
    // carrying `code` and the PROTECTED_BRANCHES-only fields, never left to a
    // bare message-only throw (see MoveRefusedError above).
    throw new MoveRefusedError(data.error || 'Failed to move', {
      status,
      code: typeof data.code === 'string' ? data.code : undefined,
      entityRoot: typeof data.entityRoot === 'boolean' ? data.entityRoot : undefined,
      needsManage: typeof data.needsManage === 'boolean' ? data.needsManage : undefined,
      wouldLoseAccess: typeof data.wouldLoseAccess === 'boolean' ? data.wouldLoseAccess : undefined,
      conflict: data.conflict === true ? true : undefined,
      damagedName: isContentDamagedCode(data.code) && typeof data.name === 'string' && data.name ? data.name : undefined,
    })
  }
  // #753: moving a FOLDER out of a received `.shared` mount is a cross-tenant
  // COPY that walks the subtree on the file-copy worker — the PUT answers 202
  // `{ queued, jobId }`. Poll it here so every caller (paste, drag) keeps its
  // "await moveFile, then toast + refresh" shape: resolve when the copy has
  // landed, throw the job's own reason when nothing could be copied.
  if (data?.queued && typeof data.jobId === 'string') {
    const result = await pollCopyJob(data.jobId)
    // #982 a folder MOVED across organisations (out of a share, into one, or between two): it moved (its source binned — the undo
    // restores that Bin entry when the whole folder went), or nothing moved and the job says why.
    if (data.moving === true) {
      // The job's result is gone (evicted before this poll): whether it moved is unknown — never "Moved".
      if (result.evicted) {
        throw new MoveRefusedError('The move finished, but its result could not be read. Refresh to see where the folder is.', { status: 200, code: 'FOLDER_MOVE_STATUS_UNKNOWN' })
      }
      if (!result.moved) {
        const e = result.errors[0]
        throw new MoveRefusedError(e?.message || 'The folder could not be moved.', { status: 409, ...(e?.code ? { code: e.code } : {}) })
      }
      const landed = result.copied[0]
      return {
        ...data, success: true,
        // `newFileRef`: the landed folder as the mover reaches it — a route through their `.shared` when it landed
        // in another organisation's share (the undo deletes it by that id).
        ...(landed ? { file: landed, movedAcrossOrgs: { newFileRef: result.moved.newFileRef ?? landed.id, sourceTenantId: result.moved.sourceTenantId, ...(result.moved.binId ? { binId: result.moved.binId } : {}) } } : {}),
        ...(result.moved.kept > 0 ? { movedKept: result.moved.kept } : {}),
      }
    }
    if (result.errors.length > 0 && result.copied.length === 0) {
      throw new Error(result.errors[0].message || 'Copy failed')
    }
    // A partial copy (something landed, something failed) resolves — the
    // caller toasts success — but carries the job's reasons so it can say so.
    return {
      ...data, success: true, copied: true, copiedItems: result.copied,
      ...(result.errors.length > 0 ? { copyErrors: result.errors } : {}),
    }
  }
  return data
}

/** `GET /api/files/move-status/:jobId` (#1167 S4). `completed` carries the move's own answer; `failed` is a 200. */
export type MoveJobStatus = JobStatus<{ status: number; body: Record<string, unknown> }>

/** Test seam: the tracker's timings (tests make it fast, or drive it with fake timers). */
export const moveJobTimings: JobTrackerTimings = { tickMs: 1_000, offlinePollMs: 2_000 }

/**
 * Follow a big folder move's `file-move` job (#1167 S4) to its stored answer — through its status route only (no
 * socket), so the shared `JOB_DEADLINE_MS` (65 min) and the tracker's 404 "not visible yet" grace apply — never
 * `pollCopyJob`'s 10-minute deadline, which would give up on a queued big move while it still runs. A job whose
 * outcome can't be learned (the deadline passed, or its status route 404s past the grace) is
 * `MOVE_JOB_STATUS_UNKNOWN` — NOT #982's `FOLDER_MOVE_STATUS_UNKNOWN` ("it finished, its result was lost"): here the
 * move may be queued, running, done or never run, so no caller records an undo and the toast says "couldn't
 * confirm". A job that failed (the move threw what the API answers 500) is a 500 refusal.
 */
async function followMoveJob(jobId: string, opts?: PinnedIdentityOptions): Promise<{ status: number; body: Record<string, unknown> }> {
  const tracker = createJobTracker<{ status: number; body: Record<string, unknown> }>({
    kind: 'move',
    onOperationEvent: () => () => {},
    isConnected: () => false,
    getStatus: (id) => request<MoveJobStatus>(`/api/files/move-status/${encodeURIComponent(id)}`, pinnedOpt(opts)),
    readResult: () => null,
    runsToCompletion: !!opts?.identity,   // #1316 review round 1: an undo's move-back finishes (D134)
  }, jobId, moveJobTimings)
  try {
    return await tracker.follow(jobId, jobId)
  } catch (err) {
    if (err instanceof JobFailedError) throw new MoveRefusedError(err.message || 'The move failed', { status: 500, code: 'MOVE_JOB_FAILED' })
    throw new MoveRefusedError('Could not confirm whether the move happened. Refresh to see where the folder is.', { status: 200, code: MOVE_JOB_STATUS_UNKNOWN_CODE })
  } finally {
    tracker.dispose()
  }
}

/** Recent `hasLinkedPointers` answers, so a retry after a slow probe converges.
 *  Small and unbounded-in-principle, but keyed by folder id and only written on
 *  a Download press, so it cannot grow faster than a person can click. */
const linkedPointerProbeCache = new Map<string, { at: number; value: boolean }>()

export { buildTusUpload, clearStaleTusSessions, type TusUploadOptions, type TusUploadResult, type TusUploadCallbacks } from './upload-tus.js'

/**
 * Trigger browser download for a blob
 */
/**
 * Poll a backgrounded file-copy job (same-tenant /copy or /cross-tenant-copy —
 * both land on the file-copy queue and report via /copy-status) until it
 * reaches a terminal state, returning the `{ copied, errors }` result.
 *
 * The deadline is generous (huge folder copies legitimately run minutes) but
 * bounded: if the worker is down / FILE_COPY_QUEUE is mismatched the job stays
 * `queued` forever and an unbounded loop would hang the paste promise. A 404
 * after a successful enqueue means the job finished and was evicted
 * (removeOnComplete) before this poll — treat as done; the caller's refresh()
 * surfaces the files.
 */
async function pollCopyJob(jobId: string, opts?: PinnedIdentityOptions): Promise<{
  copied: Array<{ id: string; name: string }>
    /** Existing files a Replace wrote a new version into (native ids; never in `copied`). */
    versioned?: Array<{ id: string; name: string; previousVersionId: string | null; versionId?: string }>
  errors: Array<{ sourceId: string; message: string; code?: string }>
  /** #982: a folder MOVED out of another organisation's share — its source binned there. */
  moved?: { sourceTenantId: string; whole: boolean; binId?: string; kept: number; newFileRef?: string }
  /** The job finished and was evicted before this poll (its result is gone). */
  evicted?: true
}> {
  const POLL_DEADLINE_MS = 10 * 60 * 1000
  const startedAt = Date.now()
  let delay = 300
  while (true) {
    await new Promise(r => setTimeout(r, delay))
    delay = Math.min(delay * 1.5, 2000)
    let status: {
      state: 'queued' | 'running' | 'completed' | 'failed'
      result?: { copied: Array<{ id: string; name: string }>; versioned?: Array<{ id: string; name: string; previousVersionId: string | null; versionId?: string }>; errors: Array<{ sourceId: string; message: string; code?: string }>; moved?: { sourceTenantId: string; whole: boolean; binId?: string; kept: number; newFileRef?: string } }
      error?: string
    }
    try {
      status = await request(`/api/files/copy-status/${jobId}`, pinnedOpt(opts))
    } catch (err) {
      if ((err as { status?: number })?.status === 404) {
        return { copied: [], errors: [], evicted: true }
      }
      throw err
    }
    if (status.state === 'completed') {
      // The executor always returns { copied, errors }; a completed job with NO
      // result is an anomaly (serialization/schema mismatch), not a clean
      // zero-file copy. Surface it instead of silently reporting "success, 0
      // files" — otherwise the user thinks the copy worked when it reported
      // nothing. (A 404 above is the DIFFERENT, benign case: completed + evicted
      // before this poll.) (U2)
      if (!status.result) {
        throw new Error('Copy finished but returned no result — refresh to verify, and retry if anything is missing.')
      }
      return status.result
    }
    if (status.state === 'failed') {
      throw new Error(status.error || 'Copy failed')
    }
    if (Date.now() - startedAt > POLL_DEADLINE_MS) {
      throw new Error('Copy is taking longer than expected — it will keep running in the background. Refresh to see the result.')
    }
  }
}

/**
 * Poll a backgrounded zip-extraction job (#282) until terminal, returning the
 * client-facing `{ fileIds, filesExtracted, bytesExtracted, destinationFolderId }`
 * shape the inline path returns. Mirrors pollCopyJob: generous but bounded
 * deadline, and a 404 after a successful enqueue means the job finished + was
 * evicted (removeOnComplete) before this poll — treat as done and let the
 * caller's refresh() surface the files. `destinationFolderId` is echoed from
 * the 202 so navigation works even on the 404 path.
 */
async function pollExtractJob(
  jobId: string,
  destinationFolderId: string | null,
): Promise<{ fileIds: string[]; topFileIds?: string[]; versioned?: Array<{ fileId: string; previousVersionId: string; versionId?: string }>; filesExtracted: number; bytesExtracted: number; destinationFolderId: string | null; skipped?: Array<{ path: string; error: string; code: string }> }> {
  const POLL_DEADLINE_MS = 10 * 60 * 1000
  const startedAt = Date.now()
  let delay = 300
  while (true) {
    await new Promise(r => setTimeout(r, delay))
    delay = Math.min(delay * 1.5, 2000)
    let status: {
      state: 'queued' | 'running' | 'completed' | 'failed'
      result?: {
        success: boolean
        error?: string
        /** Refused by the storage cap — see the throw below. */
        quotaExceeded?: boolean
        infections?: Array<{ path: string; threat: string }>
        writeBlocked?: boolean
        createdFileIds: string[]
        topCreatedFileIds?: string[]
        versioned?: Array<{ fileId: string; previousVersionId: string; versionId?: string }>
        createdFolderId: string | null
        filesExtracted: number
        bytesExtracted: number
        reservedName?: boolean
        /** #1397 G2: the archive's stored copy is damaged (nothing extracted). */
        contentDamaged?: boolean
        /** #1290: a database entry must wait or be retried — the server's mapped answer (the inline route replays it). */
        busy?: { status: number; body: { error?: string; code?: string; landing?: boolean; retry?: boolean } }
        skipped?: Array<{ path: string; error: string; code: string }>
      }
      error?: string
    }
    try {
      status = await request(`/api/files/extract-status/${jobId}`)
    } catch (err) {
      if ((err as { status?: number })?.status === 404) {
        return { fileIds: [], filesExtracted: 0, bytesExtracted: 0, destinationFolderId }
      }
      throw err
    }
    if (status.state === 'completed') {
      const r = status.result
      if (!r) {
        throw new Error('Extraction finished but returned no result — refresh to verify, and retry if anything is missing.')
      }
      // Surface the same terminal failures the inline route maps to HTTP codes.
      if (!r.success) {
        if (r.infections && r.infections.length > 0) {
          // The inline route's 422 shape (status + infections), with what was already written (#1430 (2)).
          const infected = new Error(r.error || 'Archive rejected: infected files detected') as ExtractFailureError & { infections?: unknown }
          infected.status = 422; infected.infections = r.infections
          const partial = extractPartialOf(r)
          if (partial) infected.partial = partial
          throw infected
        }
        throw extractJobFailureError(r)
      }
      return {
        fileIds: r.createdFileIds,
        ...(r.topCreatedFileIds ? { topFileIds: r.topCreatedFileIds } : {}),
        versioned: r.versioned ?? [],
        filesExtracted: r.filesExtracted,
        bytesExtracted: r.bytesExtracted,
        destinationFolderId: r.createdFolderId,
        ...(r.skipped?.length ? { skipped: r.skipped } : {}),
      }
    }
    if (status.state === 'failed') {
      throw new Error(status.error || 'Extraction failed')
    }
    if (Date.now() - startedAt > POLL_DEADLINE_MS) {
      throw new Error('Extraction is taking longer than expected — it will keep running in the background. Refresh to see the result.')
    }
  }
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/** What a person's Delete answered: done inline, or queued as a job (#781). */
export type DeleteStartResult =
  | ({ kind: 'done' } & DeleteJobOutcome)
  | { kind: 'queued'; jobId: string; operationId: string }

/** `GET /api/files/delete-status/:jobId` (#781). `failed` is a 200. */
export type DeleteJobStatus = JobStatus<DeleteJobOutcome>

/** What a person's Restore from the Bin answered: done inline, or queued as a job (#854). */
export type RestoreStartResult =
  | { kind: 'done'; fileId: string; recreatedFolders?: string[]; renamedTo?: string; restoredInto?: RestoredInto }
  | { kind: 'queued'; jobId: string; operationId: string }

/** #826: restored into the folder of its own id where it sits NOW (a manager moved it after a
 *  rebuild). In the restorer's own organisation: that folder and its full path; else empty. */
export interface RestoredInto { folderId?: string; path?: string }

/** What a Bin restore request may add to its target (#826 / the access-change guard). */
export interface RestoreRequestOptions {
  /** The person confirmed an access change (the move's guard). */
  confirmedAccessChange?: boolean
  /** Rebuild the item's missing folder where it was (the person accepted the plan). */
  rebuildPath?: boolean
  /** The fingerprint of the plan the person accepted; a different plan is refused. */
  planFingerprint?: string
  /** The session's signal (`sessionSignal()`, the Bin / Shared restores): the session ending aborts the request in
   *  flight (an `AbortError`). Not sent in the body. */
  signal?: AbortSignal
  /** An undo's restore (D134): acts as the identity of the session that started the undo; runs to completion. */
  identity?: RequestIdentity
}

function restoreRequestFlags(opts?: RestoreRequestOptions) {
  return {
    ...(opts?.confirmedAccessChange ? { confirmedAccessChange: true } : {}),
    ...(opts?.rebuildPath ? { rebuildPath: true } : {}),
    ...(opts?.planFingerprint ? { planFingerprint: opts.planFingerprint } : {}),
  }
}

/** `GET /api/bin/restore-status/:jobId` (#854). `failed` is a 200. */
export type RestoreJobStatus = JobStatus<RestoreJobOutcome>

/** What a person's Empty Bin answered: done inline (nothing to take), or queued as a job (#969). */
export type EmptyBinStartResult =
  | { kind: 'done'; deleted: number }
  | { kind: 'queued'; jobId: string; operationId: string; total: number }

/** `GET /api/bin/empty-status/:jobId` (#969). `failed` is a 200. */
export type EmptyBinJobStatus = JobStatus<EmptyBinJobOutcome>

/** #969: an Empty Bin running for this tenant, as the Bin listing reports it. */
export interface BinEmptyingJob {
  jobId: string
  operationId: string
  /** Entries in the Bin when it started. */
  total: number
}

export interface FileListItem {
  id: string
  parentId: string | null
  /** Direct parent's name — populated ONLY by the search endpoint (the
   *  results page's "location" hint); absent on folder listings. */
  parentName?: string | null
  name: string
  isFolder: boolean
  contentHash: string | null
  size: number
  mimeType: string | null
  isBinary?: boolean
  detectedType?: {
    label: string
    isText: boolean
    score: number
    mimeType: string
    mismatch: boolean
  } | null
  createdAt: string
  updatedAt: string
  /**
   * Share-primitive row kind. `'shared'` ⇒ a cross-tenant POINTER (the
   * `.shared` family) — always read-only regardless of the current
   * extension. The detail/list endpoints inline it from the raw row.
   */
  fileType?: string | null
  /**
   * Raw `metadata_json` for the row. Same-tenant listings expose it whole; the
   * cross-tenant / share listings expose only the `miniappIcon`-key subset. The
   * client reads the custom mini-app icon out of it via `parseMiniappIcon`
   * (@papan/types). Absent on rows without metadata.
   */
  metadataJson?: string | null
  /** Per-file permission override (only present when different from parent) */
  permission?: string
  /**
   * Caller's resolved permission on this file. Always populated by the list and
   * detail endpoints (server inlines `override ?? parentPermission`). Clients
   * use this to seed the `usePermission(id)` cache and skip per-file
   * `/api/files/<id>/permission` round trips.
   */
  effectivePermission?: string
  /**
   * Display name of the source tenant for a real `.shared` row, resolved
   * server-side from `metadataJson.targetTenantId`. Drives the
   * "Shared by <Org>" UI label next to the SHARED badge. Absent on
   * non-`.shared` rows, or when the source tenant has been deleted.
   */
  sharedFromName?: string
  /**
   * "Points-to" metadata for a `.chain` row, resolved server-side from the
   * same-tenant target. `chainTargetName` drives the "→ <target>" label;
   * `chainTargetId` + `chainTargetIsFolder` let the label click through to
   * the real target (navigate-into for a folder, open-viewer for a file).
   * Absent on non-chain rows, and on a chain that Needs approval (D24: it is
   * its own metadata only until approved).
   */
  chainTargetName?: string
  chainTargetId?: string
  chainTargetIsFolder?: boolean
  /**
   * True for a `.chain` whose target no longer resolves (deleted, or a
   * cross-tenant target we can't name). The UI greys it "No longer
   * available" and blocks opening; the row stays deletable.
   */
  chainBroken?: boolean
  /**
   * D17/D24: a `.chain` that still NEEDS APPROVAL — nobody (person, link or app) follows it
   * yet; it carries no target fields. Drawn
   * grey with its chain badge on every surface via `fileIconProps`. Absent otherwise.
   */
  chainNeedsApproval?: boolean
  /** D17: the caller may Approve this Needs-approval chain (edit it + open its target). */
  chainCanApprove?: boolean
  /** Owner 2026-10-27: a `.py` / `.sql` whose content the app itself (or a visitor) wrote — it doesn't run as the app
   *  until a person who may edit it approves it. Absent otherwise. */
  codeNeedsApproval?: boolean
  /** The caller may Approve this code file (they may edit it, in a browser session). */
  codeCanApprove?: boolean
  /** A FOLDER with a code file needing approval somewhere below it, that the caller can read (a warning). */
  codeApprovalBelow?: boolean
  /**
   * Issue #157 v2: a DEMOTED share copy — a plain file named `*.shared` whose
   * target already has a live canonical `.shared` elsewhere in this drive. The
   * UI shows a "Conflict" pill (visible, NOT greyed); the real share
   * gives access. Promotes back to a working `.shared` if the canonical frees.
   */
  alreadyShared?: boolean
  /** D118 (owner 2026-10-20): this app's (or this) databases.json can't be read — its databases are held back. Only for
   *  whoever can edit the app. `path` = the manifest from the app; `manifestRowId` opens it. */
  manifestProblem?: { path: string; reason: string; manifestRowId: string }
  /**
   * #781: a delete job is running on this folder (its "being deleted" marker
   * is live). The row renders as "Deleting…" for everyone, not only the tab
   * that clicked. Absent otherwise.
   */
  deleting?: boolean
  /**
   * `creator` = the person who created the row (`files.created_by`, #354) —
   * Details only, never an access fact (creating grants nothing, #1037); sent
   * only to viewers who can see the row's access. Access-column data:
   * `accessSummary` = DIRECT grants/links on this row — except on a LOCKED row
   * (`floor` set), whose teams/people are the grantees that EFFECTIVELY reach
   * it, inherited ones included (#756, same resolver as the bar) — present only
   * when the viewer can manage the row, so grant lists don't leak to
   * read-only members. `people` = up to 3 grantee emails; `more` = remaining
   * principals (incl. role/team grants); `links` = active share links.
   */
  creator?: { name: string; email?: string }
  accessSummary?: {
    /** Granted teams, ranked (yours first, parent > sub) — lead the facepile. */
    teams: { name: string; isMine: boolean }[]
    teamCount: number
    /** Named user grantees (up to 3); `peopleCount` is the total. Role grants
     *  are NOT counted here — they're floor-level (the lock conveys them). */
    people: string[]
    peopleCount: number
    links: number
    outsiders: number
  }
  /** Per-row access FLOOR — `'member'` (viewer excluded, member+ can read) or
   *  `'admin'` (members locked out). Undefined = viewer / normal. Drives the
   *  Access column's Member+ / Limited label + lock. Serialized for every viewer
   *  (coarse); the grantee facepile in accessSummary stays manager-gated. */
  floor?: 'member' | 'admin'
}

/**
 * Breadcrumb access-bar summary (drive redesign 2026-07-10). Reports the
 * access FLOOR (lowest role that can read the folder) + explicit grantees
 * when members are locked out. `scope: 'personal'` short-circuits on
 * personal tenants (no roles → bar hidden).
 */
export type FolderAccessSummary =
  | { scope: 'personal' }
  | {
      scope: 'org'
      /** Lowest org role that can read this folder. */
      floor: 'viewer' | 'member' | 'admin' | 'owner'
      /** Regular members can't get in (floor is admin/owner). */
      locked: boolean
      /** Explicit team grants, ranked (your teams first, parent > sub). */
      teams: { name: string; isMine: boolean }[]
      teamCount: number
      /** Direct user grantee emails (avatars); `getInitials`/`avatarBg` client-side. */
      users: string[]
      userCount: number
      /** Active share links on this folder. */
      links: number
      /** Distinct external recipients ("outsiders") this folder is shared out to. */
      outbound: number
      canManage: boolean
    }

export interface FileVersion {
  id: string
  fileId: string
  versionNumber?: number
  contentHash: string
  size: number
  createdBy: { type: string; id: string; name: string; email?: string } | null
  source: string
  description?: string
  createdAt: string
}

/** One captured snapshot of a `.pdapp` folder's source contents (#612). */
export interface AppVersion {
  id: string
  versionNumber: number
  createdAt: string
  source: string
  /** Total source files captured in this snapshot (the app's size). */
  fileCount: number
  /** How many source files changed vs the previous version; `null` for the
   *  first (no previous), where `fileCount` is shown instead. */
  changedCount: number | null
  createdBy: { type: string; id: string; name: string; email?: string } | null
  /** The app's main HTML at this snapshot (`index.html`, else the first
   *  root-level `.html`) as `{ fileId, versionId }` — for previewing the page
   *  as it was at this version via the per-file version preview. `null` when
   *  the app has no root HTML file. */
  previewFile: { fileId: string; versionId: string } | null
}

export type FilePermissionStr =
  | 'owner'
  | 'admin'
  | 'readwrite'
  | 'readcopy'
  | 'read'
  | 'hidden'
  | 'none'

export interface BinItem {
  id: string
  /** Tenant the bin row lives in. For My Bin items this matches the
   *  caller's current tenant; for Shared Bin items it's the source
   *  tenant that owns the deleted row. */
  tenantId: string
  originalParentId: string | null
  originalName: string
  isFolder: boolean
  totalSize: number
  deletedAt: string
  deletedBy: { type: string; id: string; name: string } | null
  expiresAt: string
  /** Caller's resolved permission at the item's original location.
   *  UI uses this to disable the Restore button when the caller can
   *  see (canRead) but can't restore (canWrite). Server re-checks on
   *  the restore call — this is a UX hint, not the gate. */
  effectivePermission: FilePermissionStr
  /** #854: a background restore holds this entry — the row shows "Restoring…"
   *  in every window. Absent from an older server: read as false. */
  restoring?: boolean
}

/** #894: where a Bin section stands — the server pages each section. */
export interface BinSectionMeta {
  /** Entries in the section this caller can see. A lower bound when
   *  `totalIsLowerBound` (shown as "N+"): a non-owner's count stops scanning. */
  total: number
  totalIsLowerBound: boolean
  /** Pass to `listBinSection` for the next page; null when there is none. */
  nextCursor: string | null
}

/** Response shape of GET /api/bin (Phase 4 — two sections; #894 — each the
 *  section's FIRST page, with its total and cursor). The meta fields are
 *  absent from an older server, which sent every entry: read them as
 *  "total = items loaded, no next page". */
export interface BinResponse {
  myBin: BinItem[]
  myBinPage?: BinSectionMeta
  sharedBin: {
    byTenant: Record<string, { name: string; items: BinItem[] } & Partial<BinSectionMeta>>
  }
  /** #969: an Empty Bin is running for this tenant. */
  emptying?: BinEmptyingJob
}

/** #894: one more page of one Bin section. */
export interface BinSectionPage {
  items: BinItem[]
  nextCursor: string | null
}

export interface QuarantineItem {
  id: string
  filename: string
  originalParentId: string | null
  quarantinedAt: string
  threat: string
  scanner: string
  size: number
}

/**
 * Error thrown when file upload is rejected due to malware detection
 */
export class MalwareDetectedError extends Error {
  threat: string
  scanner: string

  constructor(threat: string, scanner: string) {
    super(`File rejected: malware detected (${threat})`)
    this.name = 'MalwareDetectedError'
    this.threat = threat
    this.scanner = scanner
  }
}

/** Row shape of `GET /api/files/recent` (drive Home "Recently modified"). */
export interface RecentFileItem {
  id: string
  parentId: string | null
  /** Direct parent's name — the "location" hint on the Home list. */
  parentName: string | null
  name: string
  isFolder: boolean
  size: number
  mimeType: string | null
  updatedAt: string
  /** Last modifier (files.updated_by) — null for legacy rows. */
  updatedBy: { type: string; id: string; name: string } | null
  effectivePermission: string
}

/** #653 — `GET /api/files/recent-work`: recent changes grouped into pieces of work. */
export type RecentWorkScope = 'everyone' | 'mine'
export type RecentWorkFilter = 'all' | 'edited' | 'uploaded' | 'ai'
export type RecentChangeKind = 'edited' | 'uploaded' | 'ai'
/** Who made a group's changes. Never an email or an internal id. */
export type RecentWorkActor =
  | { kind: 'person'; name: string; isYou: boolean }
  | { kind: 'guest' }
  | { kind: 'app'; name: string }
export interface RecentWorkCrumb {
  id: string
  name: string
  /** An app's icon fields only — feeds `miniappIconProps`. */
  metadataJson?: string | null
}
export interface RecentWorkFile {
  id: string
  name: string
  parentId: string | null
  /** The folder it is in (`null` = the top of the drive). */
  folder: RecentWorkCrumb | null
  mimeType: string | null
  at: string
  change: RecentChangeKind
}
export interface RecentWorkGroup {
  id: string
  /** `app`: a PDApp, always one row, never its files. */
  kind: 'app' | 'project' | 'folder'
  /** The app, project or folder. `null` = the top of the drive. */
  place: RecentWorkCrumb | null
  /** From the top of the drive down to and including the place. */
  path: RecentWorkCrumb[]
  actor: RecentWorkActor
  withAI: boolean
  latestAt: string
  oldestAt: string
  /** Visible files changed (0 for an app). */
  fileCount: number
  /** Up to 50, newest first (empty for an app). */
  files: RecentWorkFile[]
}
export interface RecentWorkPage { groups: RecentWorkGroup[]; nextCursor: string | null }

/** `replace` = Replace: on every path a new VERSION of the existing file (owner, 2026-10-06). The
 *  server still accepts the legacy `addVersion` (identical); the client never sends it. */
/** `POST /:id/replace-by-move`'s answer. */
export type ReplaceByMoveResult = {
  fileId: string
  versionId: string
  previousVersionId: string | null
  fork: boolean
  /** The moved file's Bin entry (null for a fork). */
  deleted: { binId?: string; binIds: string[]; binnedFileIds: string[] } | null
}

export type ConflictStrategy = 'replace' | 'skip' | 'keepBoth'

/** One entry of the /api/files/check-conflicts response. `isFolder`
 *  describes the EXISTING destination item, not the incoming one. */
export type ConflictEntry = { name: string; parentId: string | null; isFolder: boolean; isPointer?: boolean
  /** The EXISTING item's id — native, or the compound id through a mount (what a Replace targets). */
  existingId?: string }

/** Why the server kept a split-move shell (`POST /:id/discard-split-shell`). */
const SPLIT_SHELL_409_REASONS = ['not_shell', 'not_creator', 'expired', 'has_shares', 'not_empty'] as const
export type SplitShellDiscardRefusal = typeof SPLIT_SHELL_409_REASONS[number] | 'not_found' | 'forbidden'

/**
 * D24 — the listing answered for a folder that is a `.chain` still Needing approval (or
 * reached through one): nobody follows it, so its EMPTY `files` is not an empty folder.
 * The server marks it `mount.reason === 'needs-approval'` (mount-listing.ts).
 */
export function listingNeedsApproval(result: { mount?: { reason?: string } } | null | undefined): boolean {
  return result?.mount?.reason === 'needs-approval'
}

export const filesApi = {
  // ─── Listing ───────────────────────────────────────────────────────

  /**
   * List children of a folder (paginated, sortable)
   * parentId = null means root (/papan/)
   */
  async listChildren(parentId: string | null, options?: {
    limit?: number
    offset?: number
    sortBy?: 'name' | 'modified' | 'size' | 'type'
    sortDir?: 'asc' | 'desc'
    findWritable?: boolean
  } & PinnedIdentityOptions): Promise<{
    files: FileListItem[]
    total: number
    hasMore: boolean
    parentPermission?: string
    /**
     * Present when `parentId` is a `.shared`/`.chain` mount: `'broken'` means the
     * kernel could not resolve it for this caller (revoked/hidden/deleted source),
     * so the empty `files` list means NO ACCESS — not an empty folder. Callers that
     * render an empty state must check this, or they report a denial as absence.
     * `reason === 'needs-approval'` (D24): the folder is a `.chain` that Needs approval
     * (or is reached through one) — see `listingNeedsApproval`.
     */
    mount?: { state?: string; reason?: string; reshareBlocked?: boolean }
    writableFolders?: { id: string; name?: string; parentId?: string | null; note?: string }[]
  }> {
    let url = '/api/files?'
    if (parentId) url += `parentId=${encodeURIComponent(parentId)}&`
    if (options?.limit) url += `limit=${options.limit}&`
    if (options?.offset) url += `offset=${options.offset}&`
    if (options?.sortBy) url += `sortBy=${options.sortBy}&`
    if (options?.sortDir) url += `sortDir=${options.sortDir}&`
    if (options?.findWritable) url += `findWritable=true&`
    return request(url.replace(/[&?]$/, ''), pinnedOpt(options))
  },

  /**
   * Recursively search the permitted file tree under `rootId` (default:
   * drive root) for files/folders whose name contains `query`. Powers the
   * picker dialogs' global search box. Results are already permission-
   * filtered server-side and carry `effectivePermission` inline, exactly
   * like `listChildren`. `truncated` is true when the raw match hit the
   * server's result cap.
   */
  async searchFiles(query: string, options?: {
    rootId?: string | null
    limit?: number
  }): Promise<{
    files: FileListItem[]
    total: number
    truncated: boolean
  }> {
    let url = `/api/files/search?q=${encodeURIComponent(query)}&`
    if (options?.rootId) url += `rootId=${encodeURIComponent(options.rootId)}&`
    if (options?.limit) url += `limit=${options.limit}&`
    return request(url.replace(/[&?]$/, ''))
  },

  /**
   * Tenant-wide recently-modified files, newest first (drive Home).
   * Permission-filtered server-side with the same hidden/restricted rules
   * as search; carries `effectivePermission` inline like `listChildren`.
   */
  async listRecent(options?: { limit?: number }): Promise<{ files: RecentFileItem[] }> {
    const limit = options?.limit ? `?limit=${options.limit}` : ''
    return request(`/api/files/recent${limit}`)
  },

  /** #653 — recent changes grouped into pieces of work, newest first; `cursor` from the previous page. */
  async listRecentWork(options: { scope: RecentWorkScope; filter: RecentWorkFilter; limit: number; cursor?: string | null }): Promise<RecentWorkPage> {
    const q = new URLSearchParams({ scope: options.scope, filter: options.filter, limit: String(options.limit) })
    if (options.cursor) q.set('cursor', options.cursor)
    return request(`/api/files/recent-work?${q.toString()}`)
  },

  // ─── File Info ─────────────────────────────────────────────────────

  /**
   * Get file/folder info by ID (includes computed path)
   */
  async getFileInfo(fileId: string, opts?: PinnedIdentityOptions): Promise<FileListItem & { path: string }> {
    return request(`/api/files/${fileId}`, pinnedOpt(opts))
  },

  /**
   * Existence **probe** — not a strict "does this row exist" check.
   *
   * Returns true on 200, false on any error (404, 403, network blip,
   * 5xx). The false branch is intentionally coarse (see below).
   *
   * **Don't reuse for permission decisions** — a 403 returns false
   * here even though the row exists, so callers needing the
   * exists-vs-permission distinction must hit the endpoint directly
   * and inspect the status. Today's callers (both in undoHandlers.ts,
   * the delete undo) read false as "can't confirm the file is back" —
   * the conservative answer for each: rethrow the restore's own 404,
   * or "could not confirm the undo" (#921) — hence the swallow.
   *
   * Added 2026-05-18 for the delete-undo 404 disambiguation (issue
   * #81 follow-up): tells "already-restored" apart from "purged"
   * when restoreFromBin returns 404.
   */
  /** D134 — STRICT: is the item gone? `true` only when the read answers 404; `false` when it reads fine; any other
   *  failure (a refusal, a 5xx, a rate limit, the network) is THROWN — "can't tell" never means "gone". */
  async fileGone(fileId: string, opts?: PinnedIdentityOptions): Promise<boolean> {
    try {
      await request(`/api/files/${fileId}`, opts?.identity ? { identity: opts.identity } : {})
      return false
    } catch (err) {
      if ((err as { status?: number } | null)?.status === 404) return true
      throw err
    }
  },

  async fileExists(fileId: string, opts?: PinnedIdentityOptions): Promise<boolean> {
    try {
      await request(`/api/files/${fileId}`, opts?.identity ? { identity: opts.identity } : {})
      return true
    } catch {
      return false
    }
  },

  /**
   * Get the caller's effective permission on a file.
   * Returns 'owner' | 'readwrite' | 'read' | 'hidden', or throws on 403/404.
   */
  async getPermission(fileId: string): Promise<string> {
    const res = await request<{ permission: string }>(`/api/files/${fileId}/permission`)
    return res.permission
  },

  /**
   * The caller's effective permission on several ids in ONE request (#1259) — `null` where
   * `getPermission` would throw 403. Go through `loadPermission` (queries.ts), which batches and
   * falls back to `getPermission` on a server without this route.
   */
  async getPermissions(fileIds: string[]): Promise<Record<string, string | null>> {
    const res = await request<{ permissions?: Record<string, string | null> }>(`/api/files/permissions?ids=${fileIds.map(encodeURIComponent).join(',')}`)
    if (!res.permissions) throw Object.assign(new Error('permissions batch unsupported'), { status: 404 })
    carryActingStamp(res, res.permissions) // the email these answers were read as (#1210)
    return res.permissions
  },

  /**
   * Is this id a FOLDER? (#788)
   *
   * Every `openFileById` handler has to ask before it can route: a `.pdapp`
   * FOLDER must open the folder, not launch the app. The answer used to be
   * guessed per call site — a bare catch that defaulted to "file" — which quietly
   * restored the very divergence #788 removed whenever the network hiccuped,
   * turning a fixed behaviour into a function of network weather.
   *
   * NO retry of its own. `request()` already retries a STALLED GET
   * (`getWithStallRetry`, 8s × 2), so a loop here would push the wait in front of
   * a `window.open` to ~32s — well past the ~5s of transient user activation a
   * click grants, which is how the tab gets blocked — and would re-issue a
   * deterministic 403 for nothing.
   *
   * `null` means "could not tell". Every caller then chooses "file", because a
   * file view of a folder shows an error while a wrong launch RUNS someone's app.
   */
  async isFolderById(fileId: string): Promise<boolean | null> {
    try {
      return (await filesApi.getFileInfo(fileId)).isFolder
    } catch {
      return null
    }
  },

  /**
   * Get the collab room key for a file. If the file belongs to a CSV workbook group,
   * returns the shared workbook room key instead of the individual file room.
   */
  async getCollabRoom(fileId: string): Promise<{ room: string; isWorkbookGroup: boolean }> {
    return request<{ room: string; isWorkbookGroup: boolean }>(`/api/files/${fileId}/collab-room`)
  },

  /**
   * Get breadcrumbs for a file/folder
   */
  async getBreadcrumbs(fileId: string): Promise<Array<{ id: string; name: string }>> {
    const res = await request<{ breadcrumbs: Array<{ id: string; name: string }> }>(
      `/api/files/${fileId}/breadcrumbs`
    )
    return res.breadcrumbs
  },

  /**
   * Resolve a virtual path to a file ID. Absolute (`/papan/folder/file`) by default;
   * pass `baseId` to resolve a path RELATIVE to that folder (the SDK `openFileByRelativePath`
   * form). `..` climbs FREELY toward the Papan drive root (clamped there); the resolved target
   * is access-gated server-side (`canSee`), not the path shape.
   */
  async resolvePath(path: string, baseId?: string): Promise<{ id: string; file: FileListItem } | null> {
    try {
      const q = baseId ? `?path=${encodeURIComponent(path)}&baseId=${encodeURIComponent(baseId)}` : `?path=${encodeURIComponent(path)}`
      return await request(`/api/files/resolve${q}`)
    } catch (e) {
      if (isSessionEndedError(e)) throw e   // #1316: a dropped answer is not "no such path" — callers ask again
      return null
    }
  },

  // ─── Content ───────────────────────────────────────────────────────

  /**
   * Get file content by ID
   */
  async getFileContent(fileId: string, versionId?: string): Promise<Blob> {
    let url = `/api/files/${fileId}/content`
    if (versionId) url += `?version=${versionId}`
    // #1316: a viewer stays open across an identity switch; content dropped because the session ended is asked again
    // ONCE, now as the new session (its answer is that session's view of the file) — never "the session ended" as the
    // viewer's error.
    const res = await fetchRaw(url).catch((e) => (isSessionEndedError(e) ? fetchRaw(url) : Promise.reject(e)))
    if (!res.ok) {
      // #1397: a damaged stored copy answers its own sentence (409 CONTENT_DAMAGED) — show it, not "File not found"
      const damaged = damagedMessageFrom(await res.json().catch(() => null))
      throw new Error(damaged ?? 'File not found')
    }
    return res.blob()
  },

  /** Get structured preview data (e.g., SpreadsheetModel for XLSX) */
  async getPreview(fileId: string, versionId?: string): Promise<any> {
    let url = `/api/files/${fileId}/preview`
    if (versionId) url += `?version=${versionId}`
    return request(url)
  },

  /**
   * Save file content by ID
   */
  async saveFileContent(fileId: string, content: Blob | string, options?: {
    description?: string
  }): Promise<{ fileId: string; versionId: string; contentHash: string; /** #1090 — a large app database is replaced in the background (202): no version yet. */ queued?: true; recordId?: string }> {
    const blob = typeof content === 'string' ? new Blob([content], { type: 'text/plain' }) : content
    const headers: Record<string, string> = {}
    if (options?.description) headers['x-file-description'] = encodeURIComponent(options.description)

    const res = await fetchRaw(`/api/files/${fileId}/content`, {
      method: 'POST',
      body: blob,
      headers,
    })
    if (!res.ok) throw new Error('Save failed')
    return res.json()
  },

  // ─── Upload ────────────────────────────────────────────────────────

  /**
   * Upload a file to a parent folder
   */
  async uploadFile(
    parentId: string | null,
    fileName: string,
    content: Blob | File,
    options?: {
      description?: string
      conflictStrategy?: ConflictStrategy
      /** #1316 review round 2: sent as the identity that started the batch (`pinnedBatch().identity`) — never dropped (D2). */
      identity?: RequestIdentity
    }
  ): Promise<{ fileId: string; name: string; path?: string; versionId: string; skipped?: boolean; /** #1090 — a large app database is replaced in the background (202). */ queued?: true; recordId?: string }> {
    let url = `/api/files/upload?`
    if (parentId) url += `parentId=${encodeURIComponent(parentId)}&`
    // Replace goes out under its wire name (`replaceVersion`): an old server rejects it (rolling deploy).
    if (options?.conflictStrategy) url += `conflictStrategy=${encodeURIComponent(toWireConflictStrategy(options.conflictStrategy))}&`
    url = url.replace(/[&?]$/, '')

    const headers: Record<string, string> = {
      ...(options?.identity ? {} : getDefaultHeaders()),
      'x-file-name': encodeURIComponent(fileName),
    }
    if (options?.description) headers['x-file-description'] = encodeURIComponent(options.description)

    const res = await fetchRaw(url, {
      method: 'POST',
      body: content,
      headers,
      ...(options?.identity ? { identity: options.identity } : {}),
    })

    const data = await res.json()

    if (!res.ok) {
      if (data.threat) {
        throw new MalwareDetectedError(data.threat, data.scanner || 'unknown')
      }
      throw new Error(data.error || 'Upload failed')
    }

    return data
  },

  /**
   * Upload a file via the tus resumable protocol. Survives network blips,
   * bypasses Cloudflare's 100MB body cap, streams server-side without
   * buffering the assembled file.
   */
  uploadFileWithProgress(
    parentId: string | null,
    fileName: string,
    content: Blob | File,
    options?: {
      description?: string
      conflictStrategy?: ConflictStrategy
      /** Subpath under parentId — folders auto-created on server */
      path?: string
      onProgress?: (loaded: number, total: number) => void
      signal?: AbortSignal
      uploadId?: string
      /** #1316 review round 2: sent as the identity that started the batch (`pinnedBatch().identity`). */
      identity?: RequestIdentity
    }
  ): Promise<{ fileId: string; name: string; path?: string; versionId: string; skipped?: boolean }> {
    return uploadFileWithTus(parentId, fileName, content, options)
  },

  // ─── Folder ────────────────────────────────────────────────────────

  /**
   * Create a new folder. When merge=true, returns existing folder if one
   * with the same name already exists (Windows-style merge behavior).
   */
  async createFolder(parentId: string | null, name: string, options?: { merge?: boolean; keepBoth?: boolean } & PinnedIdentityOptions): Promise<{ folder: FileListItem; merged?: boolean }> {
    return request('/api/files/folder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parentId, name, merge: options?.merge, keepBoth: options?.keepBoth }),
      ...pinnedOpt(options),
    })
  },

  /**
   * Create a `.chain` (same-tenant pointer) at `parentId` targeting an
   * existing file or folder in the caller's tenant. The server defaults
   * the row name to `<target>.chain`; `name` supplies the BASE, and the
   * server appends the `.chain` suffix when it is absent — the suffix is
   * load-bearing (`presentsAsFolder` is extension-gated), so a row without
   * it would present as a plain un-enterable file.
   *
   * Returns the created row. The driving realtime `file:created` event
   * triggers the usual list-refresh path on every open drive view.
   */
  /** D10: vouch for a "Needs approval" `.chain` — the caller must be able to open and share its target (#848). */
  /** The Approve dialog's facts — who set the chain's target and which folder it points to.
   *  Answered only to someone who could approve it. */
  async getPointerApproval(fileId: string): Promise<{
    state: string
    author: { kind: string; name?: string | null; email?: string | null; linkCreatedBy?: string | null; linkCreatorEmail?: string | null; linkId?: string | null; appId?: string | null; appMetadataJson?: string | null; appCanOpen?: boolean; requesterName?: string | null }
    target: { id: string; name: string | null; path: string | null; isFolder: boolean }
  }> {
    return request(`/api/files/${encodeURIComponent(fileId)}/pointer/approval`)
  },

  /** One share link's URL — answered (else 404) only to someone with authority over the link. */
  async getShareLinkBrief(linkId: string): Promise<{ id: string; kind: string; revoked: boolean; expired: boolean; url: string }> {
    return request(`/api/share-links/${encodeURIComponent(linkId)}/brief`)
  },

  /** Approve the version the person SAW (`expectedContentHash`, from the listing) — a newer write answers 409. */
  async approveCode(fileId: string, expectedContentHash: string | null): Promise<{ ok: true; state: 'approved' }> {
    return request(`/api/files/${encodeURIComponent(fileId)}/code/approve`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expectedContentHash }),
    })
  },

  async approvePointer(fileId: string): Promise<{ ok: true; state: 'approved' }> {
    return request(`/api/files/${encodeURIComponent(fileId)}/pointer/approve`, { method: 'POST' })
  },

  async createChain(parentId: string | null, targetFileId: string, name?: string): Promise<{ chain: FileListItem }> {
    return request('/api/files/chain', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parentId, targetFileId, name }),
    })
  },

  // ─── Move / Rename (O(1)!) ─────────────────────────────────────────

  /**
   * Move and/or rename a file. Pass name for rename, parentId for move, both for move+rename.
   */
  async moveFile(
    fileId: string,
    update: {
      name?: string; parentId?: string | null; confirmedAccessChange?: boolean; confirmedChainEscape?: boolean; expandPointers?: boolean
      /** #801 — the move preview's removable ids (`protectedBranches(fileId, 'move')`), so a
       *  split move acts only on what the person saw (`PARTITION_CHANGED` otherwise) and a
       *  resent request is recognised as a replay. Absent: not compared. */
      expectRemovable?: string[]
      /** An item out of a received `.shared`: COPY it instead of moving it (the Copy → Paste). Every
       *  other call is a MOVE and says so on the wire (`move: true`) — see the server's `moveBody`. */
      copy?: boolean
    },
    /** Not part of the move: the identity an undo's move-back acts as (D134), never in the body. #1167 S4: `onQueued`
     *  fires when the server answered 202 and the move runs as a `file-move` job (a big folder) — before this
     *  resolves, which is only once the job is done. Never for an inline move. */
    opts?: PinnedIdentityOptions & { onQueued?: (info: { jobId: string }) => void },
  ): Promise<MoveFileAnswer> {
    // #1316: a raw fetch (and a job followed after it), so bound here like `request()` — an answer after the session
    // that sent it ended (sign-out, another tenant) is dropped; the move itself goes on at the server. A move with a
    // pinned identity (an undo's move-back, D134) is work that must finish: not bound (review round 1), like request().
    const session = opts?.identity ? undefined : currentSessionSignal()
    const work = (async (): Promise<MoveFileAnswer> => {
      try { // #1264: a 403 / 404 is reported (`noteRowRefused`) and rethrown
        const { copy, ...rest } = update
        // #762: one key for this move; a lost answer is asked once more with it, and the server answers the resend with
        // the first request's answer (a 202 included: the resend follows the same job) — never a second move.
        // #1167 S4: a big folder re-parent may answer 202 and run as a `file-move` job — followed below.
        const key = mintIdempotencyKey(copy ? 'copy' : 'move')
        const sent = { 'Content-Type': 'application/json', prefer: 'respond-async', ...(opts?.identity ? identityHeaders(opts.identity) : getDefaultHeaders()), [IDEMPOTENCY_KEY_HEADER]: key }
        const sentAccount = currentAccount()   // #1316: the account this is sent under
        const res = await sendOnceMore(async () => {
          const r = await fetch(`${getApiBase()}/api/files/${fileId}`, {
            method: 'PUT',
            headers: sent,
            body: JSON.stringify(copy ? rest : { ...rest, move: true }),
            credentials: 'include',
          })
          throwIfGateway(r)
          return r
        })
        recordActingIdentity(res, sent, sentAccount)   // D134: a raw fetch feeds the identity echo too
        const data = await res.json()
        if (res.status === 202 && data?.async === true && typeof data.moveJob === 'string') {
          opts?.onQueued?.({ jobId: data.moveJob })
          // The job's stored answer is the inline route's own (status + body): read the SAME way.
          const answer = await followMoveJob(data.moveJob, opts)
          return await readMoveAnswer(answer.status, answer.body) // awaited: its refusal is caught below
        }
        return await readMoveAnswer(res.status, data) // awaited: its refusal is caught below
      } catch (err) {
        // the server can't say which side was refused: re-read the folders listing the row AND the destination
        // a refusal that landed after the session ended is dropped, not reported — it would ask the NEXT session's lists to refresh (#1316)
        // (#1316 review round 2: nor a pinned step's refusal once its identity is no longer the tab's — those rows are the old identity's)
        if (!sessionEndedSince(session, err) && (!opts?.identity || isCurrentIdentity(opts.identity))) { noteRowRefused(fileId, err); noteRowRefused(update.parentId, err) }
        throw err
      }
    })()
    return session ? dropIfSessionEnded(work, session) : work
  },

  /**
   * Will renaming this `.shared` OFF its suffix auto-promote a demoted copy to the
   * live share? Read-only preview powering the rename-detach confirm (GET
   * /api/files/:id/promote-preview). Returns `{ willPromote:false }` for anything
   * that isn't a writable live `.shared` (so the caller shows no dialog).
   */
  async promotePreview(fileId: string): Promise<{ willPromote: boolean; promote?: { id: string; name: string; path?: string[]; driveName?: string | null } }> {
    return request<{ willPromote: boolean; promote?: { id: string; name: string; path?: string[]; driveName?: string | null } }>(
      `/api/files/${fileId}/promote-preview`,
    )
  },

  // ─── Copy ──────────────────────────────────────────────────────────

  /**
   * Copy files to a destination folder.
   *
   * `errors` is the per-source failure list (e.g. "Source not found",
   * unique-constraint violation, etc.). Callers should surface non-empty
   * `errors` to the user — until this field was added the server caught
   * per-source errors silently and returned `copied: []`, which is how
   * the slow-path unique-name crash hid undetected through a live
   * session.
   */
  async copyFiles(
    sourceIds: string[],
    destinationParentId: string | null,
    conflictStrategy?: ConflictStrategy,
    operationId?: string,
    /** #1316 review round 2: a pinned batch's copy (`pinnedBatch`) — sent and followed as its starter. */
    opts?: PinnedIdentityOptions,
  ): Promise<{
    copied: Array<{ id: string; name: string }>
    /** Existing files a Replace wrote a new version into (native ids; never in `copied`). */
    versioned?: Array<{ id: string; name: string; previousVersionId: string | null; versionId?: string }>
    errors: Array<{ sourceId: string; message: string }>
  }> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', [IDEMPOTENCY_KEY_HEADER]: mintIdempotencyKey('copy') }
    if (operationId) headers['x-operation-id'] = operationId
    // The copy runs as a background worker job (B8) — the POST returns a
    // jobId immediately and we poll for the terminal {copied, errors}.
    // Per-file progress keeps riding the operation:progress WS channel,
    // so callers' progress toasts are unaffected. #762: a lost answer is asked
    // again with the same idempotency key, which joins the queued job.
    const queued = await sendOnceMore(() => request<{ jobId: string }>('/api/files/copy', {
      method: 'POST',
      headers,
      // Replace goes out under its wire name (`replaceVersion`): an old server rejects it (rolling deploy).
      body: JSON.stringify({ sourceIds, destinationParentId, conflictStrategy: toWireConflictStrategy(conflictStrategy) }),
      ...pinnedOpt(opts),
    }))
    const outcome = await pollCopyJob(queued.jobId, opts)
    // #1264: a source the copy could not read may be a gone row (its delete never announced) — the
    // lists that hold it re-read its folder (copy reports per-source failures in `errors`, never throws)
    // (#1316 review round 2: not once a pinned copy's identity is no longer the tab's — those rows are the old one's)
    if (!opts?.identity || isCurrentIdentity(opts.identity)) for (const e of outcome.errors ?? []) reportListedRowRefused(e.sourceId)
    return outcome
  },

  /**
   * Copy files from another tenant to this one. Same `errors[]` channel
   * as same-tenant copy — see copyFiles for the rationale.
   *
   * `sourceOrgEmail` is the user's effective identity in the SOURCE
   * tenant at copy time. The server uses it to pick the right
   * membership when the user has multiple emails (e.g. owner-via-email-A
   * AND member-via-email-B in the same source org). Without it the
   * server falls back to the session's primary email, which can match
   * a more permissive membership than the user was actually viewing
   * with — and that leaks files the user can't see at the source into
   * the destination.
   */
  async crossTenantCopy(
    sourceTenantId: string,
    sourceIds: string[],
    destinationParentId: string | null,
    sourceOrgEmail?: string,
    conflictStrategy?: ConflictStrategy,
    operationId?: string,
    /** Copy `.shared` / `.chain` as the CONTENT they point to (a copy across tenants —
     *  the user chose it in the pointer dialog). Absent: keep pointers. */
    expandPointers?: boolean,
    /** #1316 review round 2: a pinned batch's copy (`pinnedBatch`) — sent and followed as its starter. */
    opts?: PinnedIdentityOptions,
  ): Promise<{
    copied: Array<{ id: string; name: string }>
    /** Existing files a Replace wrote a new version into (native ids; never in `copied`). */
    versioned?: Array<{ id: string; name: string; previousVersionId: string | null; versionId?: string }>
    errors: Array<{ sourceId: string; message: string }>
  }> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', [IDEMPOTENCY_KEY_HEADER]: mintIdempotencyKey('xt-copy') }
    if (operationId) headers['x-operation-id'] = operationId
    // Now a background worker job (job name 'cross-tenant-copy' on the
    // file-copy queue) — the POST returns a jobId we poll on /copy-status,
    // same as same-tenant copy. Per-file progress rides operation:progress.
    // #762: a lost answer is asked again with the same idempotency key (joins the job).
    const queued = await sendOnceMore(() => request<{ jobId: string }>('/api/files/cross-tenant-copy', {
      method: 'POST',
      headers,
      body: JSON.stringify({ sourceTenantId, sourceIds, destinationParentId, sourceOrgEmail, conflictStrategy: toWireConflictStrategy(conflictStrategy), ...(expandPointers ? { expandPointers: true } : {}) }),
      ...pinnedOpt(opts),
    }))
    return pollCopyJob(queued.jobId, opts)
  },

  /** Does a cross-tenant copy hold a `.shared` / `.chain` the caller can read? (Only then
   *  is the keep-links / copy-contents question asked.) */
  async crossTenantCopyHasPointers(
    sourceTenantId: string,
    sourceIds: string[],
    destinationParentId: string | null,
    sourceOrgEmail?: string,
    opts?: PinnedIdentityOptions,
  ): Promise<boolean> {
    const r = await request<{ anyPointers?: boolean }>('/api/files/cross-tenant-copy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sourceTenantId, sourceIds, destinationParentId, sourceOrgEmail, preflight: true }),
      ...pinnedOpt(opts),
    })
    return r.anyPointers === true
  },

  /** #801 L4 round 6 — drop an EMPTY split-move shell with no Bin entry (the split move's
   *  undo). The server checks the shell marker, that it is empty of everything and that the
   *  caller writes its parent; a refusal answers with its reason, never throws for 409. */
  async discardSplitShell(folderId: string, opts?: PinnedIdentityOptions): Promise<{ discarded: true } | { discarded: false; reason: SplitShellDiscardRefusal }> {
    try {
      return await request<{ discarded: true }>(`/api/files/${encodeURIComponent(folderId)}/discard-split-shell`, { method: 'POST', ...(opts?.identity ? { identity: opts.identity } : {}) })
    } catch (err) {
      const e = err as { status?: number; code?: string }
      const reason = e.code
      if (e.status === 409 && reason && (SPLIT_SHELL_409_REASONS as readonly string[]).includes(reason)) return { discarded: false, reason: reason as SplitShellDiscardRefusal }
      if (e.status === 404) return { discarded: false, reason: 'not_found' }
      if (e.status === 403) return { discarded: false, reason: 'forbidden' }
      throw err
    }
  },

  /** Count a folder's subtree (for copy progress) — `count` INCLUDES the folder itself
   *  (`fileCount + folderCount + 1`; an empty folder answers 1); `fileCount` /
   *  `folderCount` are the descendants only. `truncated` = the
   *  server's bounded walk stopped early, so `count` is a lower bound.
   *  `budgetMs` asks the server to stop its walk sooner (clamped server-side). */
  async countChildren(folderId: string, opts?: { budgetMs?: number } & PinnedIdentityOptions): Promise<{ count: number; fileCount: number; folderCount: number; truncated: boolean }> {
    const q = opts?.budgetMs !== undefined ? `?budgetMs=${encodeURIComponent(String(opts.budgetMs))}` : ''
    const result = await request<{ count: number; fileCount?: number; folderCount?: number; truncated?: boolean }>(`/api/files/${folderId}/count${q}`, opts?.identity ? { identity: opts.identity } : {})
    // `truncated` = the count is only a LOWER bound — never treat it as exact. The
    // descendant split is what "empty" means (`count` includes the folder itself); a
    // response without it reads as NOT empty (fail toward keeping the folder).
    return {
      count: result.count,
      fileCount: result.fileCount ?? 1,
      folderCount: result.folderCount ?? 0,
      truncated: result.truncated === true,
    }
  },

  /** Get folder/file info: descendant counts, total size, and user's permission.
   *  Figures cover only what the caller can see; `truncated` = the server's
   *  bounded walk stopped early, so they are a MINIMUM (a lower bound). */
  async getFileStats(fileId: string): Promise<{
    count: number
    fileCount: number
    folderCount: number
    totalSize: number
    truncated?: boolean
    permission: string
  }> {
    return request(`/api/files/${fileId}/count`)
  },

  /**
   * Folder access summary for the breadcrumb access bar (drive redesign).
   * `scope: 'personal'` on non-org tenants (the bar doesn't render).
   * `floor` = lowest role that can read the folder; `locked` = members are
   * shut out; `teams`/`users` populate the locked-state facepile.
   */
  async getFolderAccessSummary(folderId: string): Promise<FolderAccessSummary> {
    return request(`/api/folders/${folderId}/access-summary`)
  },

  /**
   * Save As — writes current Y.js doc content (including unsaved changes) to a new file.
   */
  async saveAs(
    sourceFileId: string,
    destParentId: string | null,
    destName: string,
    keepBoth?: boolean,
    /** #981: a large CSV's unsaved edits kept on an EARLIER content of the file (`/api/csv/:id/open`'s `staleEdits`). */
    opts?: { fromBase?: string },
  ): Promise<{ fileId: string; name: string }> {
    // Onto an existing file (no keepBoth) this is Replace: a new version of that file.
    return request('/api/files/save-as', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sourceFileId, destParentId, destName, keepBoth, ...(opts?.fromBase ? { fromBase: opts.fromBase } : {}) }),
    })
  },

  /**
   * A MOVE with Replace: the moved file's bytes become a new VERSION of the existing destination file
   * (owner, 2026-10-06 — never binned) and the SERVER sends the moved file to the Bin in the same
   * request (`deleted`) — unless `fork` (it came out of another organisation's `.shared`: the source
   * stays). `previousVersionId` → `versionId` is the undo pair.
   */
  async replaceByMove(destFileId: string, sourceId: string, opts?: PinnedIdentityOptions): Promise<ReplaceByMoveResult> {
    return request(`/api/files/${encodeURIComponent(destFileId)}/replace-by-move`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sourceId }),
      ...pinnedOpt(opts),
    })
  },

  // ─── Protected-branch preview (#801) ────────────────────────────────

  /**
   * What a delete or move of this folder would skip — the direct branches the
   * caller cannot change (and, for a move, the root's own access setup a split
   * would leave behind). `removable` is what the caller sends back as
   * `expectRemovable` to `deleteFile` / `moveFile`, so the act does exactly
   * what was previewed (a changed subtree in between is refused as
   * `PARTITION_CHANGED`, retryable).
   */
  /** #801 — the delete / move preview. A MOVE may name its destination (`dest`; `null` = the
   *  drive root): the server then also runs the move's own judgements for it and reports a
   *  refusal the act would meet as `blocked` (L4 round 6, M-17c). */
  async protectedBranches(fileId: string, op: 'delete' | 'move', dest?: string | null, opts?: PinnedIdentityOptions): Promise<PartitionPreview> {
    const d = op === 'move' && dest !== undefined ? `&dest=${encodeURIComponent(dest ?? '')}` : ''
    // #1264: the preview a person's delete / move of a listed folder asks first — a 403 / 404 here is
    // that row refused (a gone folder lands HERE, never reaching the act), so it is reported too
    // (#1316 review round 2: not a pinned batch's preview answered after its session ended — that row is the old identity's)
    if (opts?.identity && !isCurrentIdentity(opts.identity)) return request(`/api/files/${fileId}/protected-branches?op=${op}${d}`, pinnedOpt(opts))
    return reportingRowRefusal(fileId, () => request(`/api/files/${fileId}/protected-branches?op=${op}${d}`, pinnedOpt(opts)))
  },

  // ─── Delete ────────────────────────────────────────────────────────

  /**
   * Delete a file to bin
   *
   * Owns its timeout. Deleting a FOLDER deletes its whole subtree in one
   * request, and that is not a 30-second operation: 66,677 files measured at
   * 93-126s on stagetest2 (#732). Under the 30s default the client aborted
   * mid-delete and reported `deleteFailed` for a delete that had succeeded —
   * with the row still on screen, inviting the user to run it again. Same
   * reasoning, and the same fix, as `extract` below.
   *
   * The ceiling is deliberately above nginx's `/api` `proxy_read_timeout`
   * (600s) so the proxy's answer arrives first and the client reports what the
   * server actually said.
   *
   * `opts.expectRemovable` (#801) carries the preview's removable ids back so
   * a split delete acts only on what the person saw. On a WHOLE delete the
   * response keeps the legacy `binId`/`expiresAt` (now optional — a SPLIT
   * delete has neither, since the root itself is never binned); `binIds` /
   * `binnedFileIds` cover both shapes. `skipped` — the partition as the
   * caller's own listing shows it — is present only on a (non-replay) split.
   */
  async deleteFile(fileId: string, opts?: { expectRemovable?: string[] } & PinnedIdentityOptions): Promise<{
    success: true
    binId?: string
    expiresAt?: string
    binIds: string[]
    binnedFileIds: string[]
    skipped?: PartitionPreview
    /** Cross-tenant mount delete (source-routed): the bin entry landed in the SOURCE
     *  tenant, not the caller's own — whole and split alike. */
    deletedInSource?: boolean
  }> {
    // #762: keyed, and asked once more on a lost answer — the server answers the resend with the first answer.
    const key = mintIdempotencyKey('delete')
    return reportingRowRefusal(fileId, () => sendOnceMore(() => request(`/api/files/${fileId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', [IDEMPOTENCY_KEY_HEADER]: key },
      body: JSON.stringify({ expectRemovable: opts?.expectRemovable }),
      signal: AbortSignal.timeout(11 * 60_000),
      ...(opts?.identity ? { identity: opts.identity } : {}),
    })))
  },

  /**
   * The person's Delete (#781). Asks for `Prefer: respond-async`, so a big
   * folder answers 202 at once and runs in the `file-delete` job; everything
   * else answers 200 inline, exactly like `deleteFile`. Follow a queued one
   * with `trackDeleteJob`.
   *
   * `operationId` is minted by the caller BEFORE the request, so its listener
   * for the job's progress events is already in place when the job starts —
   * a job just over the threshold can finish before the 202 is read.
   *
   * Normally answers in well under a second (the inline path is bounded by
   * the 2,000-row threshold; a big folder answers 202 before any work). The
   * limit is still 2 minutes, not the default 30 s: on a stalled server a
   * late answer is still the true one, while giving up early told people a
   * delete had failed that the server then carried out. A request that gets
   * no answer at all is resolved by the caller asking again (a repeated
   * Delete by the same person joins the running job).
   */
  async deleteFileAsync(fileId: string, operationId: string, opts?: { expectRemovable?: string[] } & PinnedIdentityOptions): Promise<DeleteStartResult> {
   try { // #1264: a 403 / 404 is reported (`noteRowRefused`) and rethrown
    // #762: keyed, and asked once more on a lost answer (the server answers the resend with the first answer).
    const key = mintIdempotencyKey('delete')
    const res = await sendOnceMore(() => request<
      | (DeleteJobOutcome & { success: true })
      | { success: true; async: true; jobId: string; operationId?: string }
    >(`/api/files/${fileId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', prefer: 'respond-async', 'x-operation-id': operationId, [IDEMPOTENCY_KEY_HEADER]: key },
      // #801: the preview's removable ids, so a split acts only on what the
      // person saw (checked again by the job, for a queued delete).
      body: JSON.stringify({ expectRemovable: opts?.expectRemovable }),
      signal: AbortSignal.timeout(2 * 60_000),
      ...pinnedOpt(opts),
    }))
    if ('async' in res && res.async) {
      // The server echoes the id of the job it JOINED, which may be an earlier
      // tab's (same person, same folder) — follow that one's operation id.
      return { kind: 'queued', jobId: res.jobId, operationId: res.operationId ?? operationId }
    }
    const { success: _ok, ...done } = res as DeleteJobOutcome & { success: true }
    return { kind: 'done', ...done }
   } catch (err) { noteRowRefused(fileId, err); throw err }
  },

  /** A delete job's state (`/delete-status/:jobId`). 404 once the job is gone
   *  (kept an hour) or when it isn't the caller's. */
  async getDeleteStatus(jobId: string, opts?: PinnedIdentityOptions): Promise<DeleteJobStatus> {
    return request(`/api/files/delete-status/${encodeURIComponent(jobId)}`, pinnedOpt(opts))
  },

  // ─── Versions ──────────────────────────────────────────────────────

  /**
   * Get file versions
   */
  async getVersions(fileId: string, opts?: PinnedIdentityOptions): Promise<{ versions: FileVersion[]; resolvedFileId?: string }> {
    const res = await request<{ versions: FileVersion[]; resolvedFileId?: string }>(`/api/files/${fileId}/versions`, opts?.identity ? { identity: opts.identity } : {})
    return { versions: res.versions, resolvedFileId: res.resolvedFileId }
  },

  /**
   * Restore a specific version
   */
  /**
   * ONE undo step of Replace(s), all or nothing on the server: every file back to `previousVersionId`
   * (each only while `expectCurrentVersionId` — the version its Replace wrote — is still current) and,
   * for a move, the moved file back from the Bin. Any file changed since → 409 VERSION_CHANGED with
   * nothing restored.
   */
  async undoReplace(
    versions: Array<{ fileId: string; previousVersionId: string; expectCurrentVersionId: string }>,
    restoreSourceBinId?: string,
    opts?: PinnedIdentityOptions,
  ): Promise<{ success: true; restored: string[] }> {
    return request('/api/files/undo-replace', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ versions, ...(restoreSourceBinId ? { restoreSourceBinId } : {}) }),
      ...(opts?.identity ? { identity: opts.identity } : {}),
    })
  },

  /** `expectCurrentVersionId` (an UNDO of a Replace): restore only while that is still the file's
   *  current version — else the server answers 409 VERSION_CHANGED ("changed since"), nothing restored. */
  /** #1090 — restoring a version of a LARGE app database runs in the background: `queued` (202), the outcome arrives as
   *  a notice; `versionId` is then absent. */
  async restoreVersion(fileId: string, versionId: string, opts?: { expectCurrentVersionId?: string } & PinnedIdentityOptions): Promise<{ fileId: string; versionId?: string; queued?: true; recordId?: string }> {
    return request(`/api/files/${fileId}/restore`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ versionId, ...(opts?.expectCurrentVersionId ? { expectCurrentVersionId: opts.expectCurrentVersionId } : {}) }),
      ...(opts?.identity ? { identity: opts.identity } : {}),
    })
  },

  // ─── Version retention (per-file override, #590) ────────────────────

  /**
   * Read this file's effective version-retention: any per-file override,
   * the resolved count actually in force, and which layer of the ladder
   * (file/ext/kind/tenant/platform/shipped) decided it. `kind`/`ext` are the
   * AUTHORITATIVE values the resolver used — `kind` is `'pdapp'` (never a
   * filename-derived kind) for a file nested in a `.pdapp` folder, since that
   * atomicity can't be recovered from the filename alone client-side.
   */
  async getVersionRetention(fileId: string): Promise<{ override: number | null; resolved: number; source: string; kind: string; ext: string | null; editable?: boolean }> {
    // `editable: false` (#1493): a file reached through a route into another org — the owning org's setting, read-only
    return request<{ override: number | null; resolved: number; source: string; kind: string; ext: string | null; editable?: boolean }>(`/api/files/${fileId}/version-retention`)
  },

  /**
   * Set a per-file version-retention override (max retained versions, 1-500).
   */
  async setVersionRetention(fileId: string, maxVersions: number, opts?: { purge?: boolean }): Promise<{ ok: true } & RetentionPurgeResponse> {
    return request<{ ok: true } & RetentionPurgeResponse>(`/api/files/${fileId}/version-retention`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ maxVersions, purge: opts?.purge === true }),
    })
  },

  /** What setting (`maxVersions`) or clearing (`null`) this file's own limit would delete (#1443). Writes nothing. */
  async versionRetentionPurgePreview(fileId: string, maxVersions: number | null): Promise<RetentionPurgePreviewResponse> {
    return request<RetentionPurgePreviewResponse>(`/api/files/${fileId}/version-retention/purge-preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ maxVersions }),
    })
  },

  /**
   * Clear a per-file version-retention override — falls back to the
   * inherited ext/kind/tenant/platform/shipped ladder.
   */
  async clearVersionRetention(fileId: string, opts?: { purge?: boolean }): Promise<{ ok: true } & RetentionPurgeResponse> {
    return request<{ ok: true } & RetentionPurgeResponse>(`/api/files/${fileId}/version-retention${opts?.purge ? '?purge=1' : ''}`, { method: 'DELETE' })
  },

  // ─── Bin Operations ────────────────────────────────────────────────


  /** The first page of every Bin section, with totals (#894). */
  async listBin(): Promise<BinResponse> {
    return request<BinResponse>('/api/bin')
  },

  /**
   * #894: the next page of one Bin section — `'mine'` or a Shared Bin source
   * tenant id — after `cursor` (none = from the newest). `kind: 'shared'` keeps
   * only deleted `.shared` items.
   */
  async listBinSection(section: string, cursor?: string | null, opts: { kind?: 'shared' } = {}): Promise<BinSectionPage> {
    const params = new URLSearchParams({ section })
    if (cursor) params.set('cursor', cursor)
    if (opts.kind) params.set('kind', opts.kind)
    return request<BinSectionPage>(`/api/bin?${params.toString()}`)
  },

  /**
   * Restore a soft-deleted item from the bin.
   *
   * When no `targetParentId` is given and the item's original folder is gone, the
   * server answers 409 (`error: 'TARGET_PARENT_GONE'`, `code: 'RESTORE_NEEDS_REBUILD'`)
   * with the plan to rebuild that folder (`err.restorePlan`, #826). Re-call with
   * `rebuildPath: true` and the plan's `planFingerprint` to restore it there. The
   * old "relocate to drive root" fallback stays removed: it leaked Restricted-folder
   * contents into the publicly-visible root (issue #81 follow-up).
   */
  async restoreFromBin(binId: string, targetParentId?: string | null, opts?: RestoreRequestOptions): Promise<{ fileId: string; recreatedFolders?: string[]; renamedTo?: string; restoredInto?: RestoredInto }> {
    // E7 PR 0b: no automatic retry (owner, option B) — a 503 "still verifying" is thrown like any refusal; the session
    // ending aborts it in flight
    return request(`/api/bin/${binId}/restore`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetParentId, ...restoreRequestFlags(opts) }),
      // Inline, so a big folder takes minutes (20,000 files: 175 s on stagetest) —
      // past Cloudflare's 125 s, which then answers 524 while the restore goes on.
      // Undo uses `restoreFromBinAsync` + the job's status route instead
      // (undoHandlers.ts `restoreUntilBack`, #921). The same ceiling as
      // `deleteFile`, for the same reason: a late answer is the true one.
      signal: requestSignal(11 * 60_000, opts?.signal),
      ...(opts?.identity ? { identity: opts.identity } : {}),
    })
  },

  /**
   * The person's Restore from the Bin (#854). Asks for `Prefer: respond-async`,
   * so a big entry answers 202 at once and runs in the `file-restore` job;
   * anything smaller answers 200 inline, exactly like `restoreFromBin`. Follow
   * a queued one with `createRestoreTracker`. Refusals (the original folder is
   * gone, an access change to confirm, …) throw exactly as `restoreFromBin`'s do.
   *
   * `operationId` is minted by the caller BEFORE the request, so its listener
   * is in place when the job starts. Two minutes, like `deleteFileAsync`: the
   * answer is normally immediate, and a late one is still the true one.
   */
  async restoreFromBinAsync(
    binId: string,
    operationId: string,
    targetParentId?: string | null,
    opts?: RestoreRequestOptions,
  ): Promise<RestoreStartResult> {
    const res = await request<
      | { fileId: string; recreatedFolders?: string[]; renamedTo?: string; restoredInto?: RestoredInto }
      | { success: true; async: true; jobId: string; operationId?: string }
    >(`/api/bin/${binId}/restore`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', prefer: 'respond-async', 'x-operation-id': operationId },
      body: JSON.stringify({ targetParentId, ...restoreRequestFlags(opts) }),
      signal: requestSignal(2 * 60_000, opts?.signal),
      ...pinnedOpt(opts),
    })
    if ('async' in res && res.async) {
      // The id of the job it JOINED may be an earlier tab's — follow that one.
      return { kind: 'queued', jobId: res.jobId, operationId: res.operationId ?? operationId }
    }
    const done = res as { fileId: string; recreatedFolders?: string[]; renamedTo?: string; restoredInto?: RestoredInto }
    return {
      kind: 'done', fileId: done.fileId,
      ...(done.recreatedFolders ? { recreatedFolders: done.recreatedFolders } : {}),
      ...(done.renamedTo ? { renamedTo: done.renamedTo } : {}),
      ...(done.restoredInto ? { restoredInto: done.restoredInto } : {}),
    }
  },

  /** A restore job's state. 404 once the job is gone or when it isn't the caller's. */
  async getRestoreStatus(jobId: string, opts?: PinnedIdentityOptions): Promise<RestoreJobStatus> {
    return request(`/api/bin/restore-status/${encodeURIComponent(jobId)}`, pinnedOpt(opts))
  },

  async deleteBinItem(binId: string): Promise<void> {
    await request(`/api/bin/${binId}`, { method: 'DELETE' })
  },

  /**
   * The person's Delete forever on one Bin entry (#1167 S2). Asks for `Prefer:
   * respond-async`, so a big entry answers 202 at once and runs in the
   * `bin-purge-item` job; anything smaller is deleted inline (200), exactly like
   * `deleteBinItem`. Follow a queued one with `runBinPurgeJob` / `createBinPurgeTracker`.
   * The same person asking again JOINS the running job (its jobId and operation
   * id); anyone else gets 409 `BIN_ITEM_BEING_DELETED`.
   */
  async deleteBinItemAsync(binId: string, operationId: string): Promise<BinPurgeStartResult> {
    const res = await request<
      | { success: true; blobsRemoved?: number }
      | { success: true; async: true; jobId: string; operationId?: string }
    >(`/api/bin/${binId}`, {
      method: 'DELETE',
      headers: { prefer: 'respond-async', 'x-operation-id': operationId },
      signal: AbortSignal.timeout(2 * 60_000),
    })
    if ('async' in res && res.async) {
      // The id of the job it JOINED may be an earlier tab's — follow that one.
      return { kind: 'queued', jobId: res.jobId, operationId: res.operationId ?? operationId }
    }
    return { kind: 'done' }
  },

  /** A Delete forever job's state. 404 once the job is gone or when it isn't the caller's. */
  async getBinPurgeStatus(jobId: string): Promise<BinPurgeJobStatus> {
    return request(`/api/bin/purge-status/${encodeURIComponent(jobId)}`)
  },

  /**
   * Hide a bin item from THIS user's view without affecting recovery
   * for anyone else. Server records the dismissal per-user; idempotent.
   * Useful for items the caller can see but can't act on (future cross-
   * tenant mount-induced deletes).
   */
  async dismissBinItem(binId: string): Promise<void> {
    await request(`/api/bin/${binId}/dismiss`, { method: 'POST' })
  },

  /**
   * Is this Bin entry still there? Only a 404 says it is gone (false); any other failure — a 5xx, a network error, a
   * 401 — is not an answer and THROWS (#921 code review: mapping every error to "gone" told people their file was
   * permanently deleted on a 502).
   */
  async binItemExists(binId: string, opts?: PinnedIdentityOptions): Promise<boolean> {
    try {
      await request(`/api/bin/${binId}`, opts?.identity ? { identity: opts.identity } : {})
      return true
    } catch (err) {
      if ((err as { status?: number })?.status === 404) return false
      throw err
    }
  },

  /**
   * The person's Empty Bin (#969). Asks for `Prefer: respond-async`, so the
   * purge runs in the `bin-empty` job and this answers at once (202); follow it
   * with `createEmptyBinTracker`. A Bin with nothing to take answers 200
   * inline. A second Empty Bin while one runs JOINS it: the answer names that
   * job (and its operation id, which may be another tab's or person's).
   */
  async emptyBinAsync(operationId: string): Promise<EmptyBinStartResult> {
    const res = await request<
      | { deleted: number }
      | { success: true; async: true; jobId: string; operationId?: string; total: number }
    >('/api/bin', {
      method: 'DELETE',
      headers: { prefer: 'respond-async', 'x-operation-id': operationId },
      signal: AbortSignal.timeout(2 * 60_000),
    })
    if ('async' in res && res.async) {
      return { kind: 'queued', jobId: res.jobId, operationId: res.operationId ?? operationId, total: res.total }
    }
    return { kind: 'done', deleted: (res as { deleted: number }).deleted }
  },

  /** An Empty Bin job's state. 404 once the job is gone, or for someone who may not empty the Bin. */
  async getEmptyBinStatus(jobId: string): Promise<EmptyBinJobStatus> {
    return request(`/api/bin/empty-status/${encodeURIComponent(jobId)}`)
  },

  // ─── Conflict Checks ──────────────────────────────────────────────

  /**
   * Check which names conflict in given parent folders. Each conflict
   * carries `isFolder` describing the *existing* destination item, so
   * UIs can render "Folder Already Exists" vs "File Already Exists"
   * correctly instead of hardcoding one or the other.
   */
  async checkConflicts(
    names: Array<{ name: string; parentId: string | null }>,
    opts?: PinnedIdentityOptions,
  ): Promise<ConflictEntry[]> {
    // The server caps `names` at MAX_OPERATION_ITEMS (1000) per request —
    // a >1000-file folder upload used to 400 on this pre-flight. Chunk
    // sequentially; the server answers each chunk with a single batched
    // query.
    const CHUNK = 500
    const conflicts: ConflictEntry[] = []
    for (let i = 0; i < names.length; i += CHUNK) {
      const res = await request<{ conflicts: ConflictEntry[] }>(
        '/api/files/check-conflicts',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ names: names.slice(i, i + CHUNK) }),
          ...pinnedOpt(opts),
        }
      )
      conflicts.push(...res.conflicts)
    }
    return conflicts
  },

  // ─── Download ──────────────────────────────────────────────────────

  /**
   * Download files/folders (triggers browser download).
   *
   * Single-file path uses a GET URL + `<a href download>` so the browser
   * streams natively to disk — the download appears in the browser's
   * download bar/history with live progress from byte 1. This is industry-
   * standard (Drive / Dropbox / S3). The previous `fetch → res.blob() →
   * URL.createObjectURL` pattern silently buffered the entire body in JS
   * memory before showing a save dialog, producing "nothing happens for a
   * minute, then suddenly the file appears" UX on multi-GB files.
   *
   * Multi-file / folder downloads stay on the POST path: `<a href>` can't
   * carry a request body, so the GET endpoint only handles single fileIds.
   */
  async download(
    fileIds: string[],
    opts?: {
      containsFolder?: boolean
      /** Suggested filename for the save dialog (folder/multi path). */
      suggestedName?: string
      /** Streamed-download progress (bytes written to disk); only fires on the
       *  File System Access path. Total is unknown (chunked zip, no Content-Length). */
      onProgress?: (bytesWritten: number) => void
      /**
       * Replace each `.shared`/`.chain` inside this download with a NORMAL
       * FOLDER holding what it points at, so the whole selection is one archive.
       *
       * Off, a pointer rides as its own `metadata_json` file and the export
       * round-trips back into a share. On, the archive is a plain copy. That is
       * the question the download dialog asks.
       */
      expandPointers?: boolean
    },
  ): Promise<{ cancelled: boolean }> {
    // GET /:fileId/download streams a single file natively via <a href download>.
    // It rejects folders with 400 — and because <a> can't read the response,
    // the browser silently saves the JSON error body to disk as `download.json`.
    // Route folders through the POST zip path instead.
    if (fileIds.length === 1 && !opts?.containsFolder) {
      const tenantId = getActiveTenantId()
      const orgEmail = getActiveOrgEmail()
      const params = new URLSearchParams()
      if (tenantId) params.set('tenantId', tenantId)
      if (orgEmail) params.set('orgEmail', orgEmail)
      const qs = params.toString() ? `?${params.toString()}` : ''
      const url = `${getApiBase()}/api/files/${encodeURIComponent(fileIds[0])}/download${qs}`
      // Pre-flight HEAD: the native `<a href download>` below can't observe HTTP
      // status, so a 403/404 would silently save the error body to disk. Check
      // first and throw so the caller surfaces a toast (same as the POST path).
      const headSent = getDefaultHeaders()
      const headSentAccount = currentAccount()   // #1316: the account this is sent under
      const head = await fetch(url, { method: 'HEAD', credentials: 'include', headers: headSent })
      recordActingIdentity(head, headSent, headSentAccount)
      if (!head.ok) {
        throw new Error(
          head.status === 403 ? "You don't have permission to download this file."
            : head.status === 404 ? 'This file no longer exists.'
            : `Download failed (${head.status})`
        )
      }
      // Synthetic `<a href download>` click. The server's Content-Disposition
      // sets the filename, so we leave `a.download` empty rather than guess.
      const a = document.createElement('a')
      a.href = url
      a.rel = 'noopener'
      a.download = ''
      document.body.appendChild(a)
      a.click()
      a.remove()
      return { cancelled: false }   // native save bar — nothing to cancel
    }

    const suggestedName = opts?.suggestedName || `download-${Date.now()}.zip`

    // Prefer STREAMING the zip straight to disk via the File System Access API:
    // constant memory (the old `res.blob()` buffered the ENTIRE zip in tab memory
    // → OOM on a large folder) and an immediate save dialog (the old path showed
    // nothing for a minute → users double-clicked). The file handle MUST be
    // acquired inside the click gesture, BEFORE `fetch` consumes it. Unsupported
    // browsers (Firefox/Safari) fall back to the blob path below.
    type MinimalWritable = { write(chunk: Uint8Array): Promise<void>; close(): Promise<void>; abort?(): Promise<void> }
    const picker = (window as unknown as {
      showSaveFilePicker?: (o: unknown) => Promise<{ createWritable(): Promise<MinimalWritable> }>
    }).showSaveFilePicker
    let writable: MinimalWritable | null = null
    if (typeof picker === 'function') {
      try {
        // Derive the picker's accepted type from the suggested name rather than
        // hardcoding zip: this path also serves a FILE-target `.shared`, whose
        // download is the file itself (`index.html`), and a zip-only `types`
        // makes Chrome force a `.zip` extension onto it. Every pre-existing
        // caller passes a `.zip` name (or none, which defaults to one), so the
        // zip branch stays byte-identical for them.
        const pickerExt = /\.[^./\\]+$/.exec(suggestedName)?.[0]?.toLowerCase()
        const handle = await picker({
          suggestedName,
          // No extension (`Makefile.shared` -> `Makefile`) means we cannot name a
          // type, so omit `types` entirely and let the picker accept anything.
          // Falling back to the zip branch would make Chrome save `Makefile.zip`.
          ...(pickerExt === '.zip'
            ? { types: [{ description: 'Zip archive', accept: { 'application/zip': ['.zip'] } }] }
            : pickerExt
              ? { types: [{ description: 'File', accept: { 'application/octet-stream': [pickerExt] } }] }
              : {}),
        })
        writable = await handle.createWritable()
      } catch (e) {
        // User dismissed the save dialog → abort the whole download (don't silently
        // fall back to a surprise blob download they didn't ask for).
        if ((e as { name?: string })?.name === 'AbortError') return { cancelled: true }
        // The picker EXISTS and refused — almost always because the click's
        // transient activation (~5s) was spent before we got here. Falling back
        // to the blob path would buffer the WHOLE archive in tab memory, which
        // is the OOM this route streams specifically to avoid (gh #57), and it
        // would do it silently on the biggest folders — the ones whose
        // pre-flight walk is slowest, so precisely the ones that lose the
        // activation. Report it instead; the caller asks for another click.
        //
        // NOT the same as "this browser has no File System Access API".
        // Firefox and Safari never enter this block at all: `picker` is
        // undefined there and the blob path below is their only route.
        throw new PickerBlockedError()
      }
    }

    let res: Response
    try {
      const dlSent = { 'Content-Type': 'application/json', ...getDefaultHeaders() }
      const dlSentAccount = currentAccount()   // #1316: the account this is sent under
      res = await fetch(`${getApiBase()}/api/files/download`, {
        method: 'POST',
        headers: dlSent,
        body: JSON.stringify({ fileIds, expandPointers: opts?.expandPointers }),
        credentials: 'include',
      })
      recordActingIdentity(res, dlSent, dlSentAccount)
    } catch (e) {
      // Network-level fetch rejection AFTER the FSA writable was opened — abort it
      // so we don't strand an orphaned 0-byte file at the user's picked path. (#298 low #7)
      if (writable) await writable.abort?.().catch(() => {})
      throw e
    }

    if (!res.ok) {
      if (writable) await writable.abort?.().catch(() => {})
      throw new Error('Download failed')
    }

    if (writable && res.body) {
      // Pump the response to disk with backpressure (awaiting each write) so peak
      // memory is one chunk, not the whole archive.
      let written = 0
      const reader = res.body.getReader()
      try {
        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          if (value) {
            await writable.write(value)
            written += value.byteLength
            opts?.onProgress?.(written)
          }
        }
        await writable.close()
      } catch (e) {
        await writable.abort?.().catch(() => {})
        throw e
      }
      return { cancelled: false }
    }

    // Fallback (no File System Access API, or a 200 with a null body): buffer to a
    // blob then save. If a writable WAS opened but res.body came back null, abort it
    // first so the picked path isn't left as an orphaned 0-byte stub. (#298 low #7)
    if (writable) await writable.abort?.().catch(() => {})
    const filename = parseContentDispositionFilename(res.headers.get('Content-Disposition'), suggestedName)
    const blob = await res.blob()
    triggerDownload(blob, filename)
    return { cancelled: false }
  },

  /**
   * Does downloading these folders involve any `.shared`/`.chain` at all?
   *
   * Answered by the download route itself (`manifestOnly`), so it comes from
   * the SAME permission walk that builds the archive — a separate endpoint
   * could drift and say yes about something the zip omits.
   *
   * A BOOLEAN, deliberately. The route can stop at the first pointer it finds
   * (`anyPointers`), so the list it returns is truncated by design; handing it
   * back would invite a caller to trust a preview that is not one. The full
   * manifest still exists on the route for anything that genuinely needs it.
   *
   * This runs between the user's CLICK and `showSaveFilePicker()` on every
   * folder download, so its cost is bounded by the click's ~5s transient
   * activation, not by patience. An earlier version of this comment claimed
   * "ordinary folder downloads never do" — true when a separate menu item
   * called it, false the moment detection became automatic, and that gap is
   * what put large downloads on the buffer-it-all path.
   */
  async hasLinkedPointers(fileIds: string[], opts?: PinnedIdentityOptions): Promise<boolean> {
    // Answered from cache when we have asked recently, because the FIRST answer
    // may arrive too late to be useful. The probe runs between the click and
    // `showSaveFilePicker()`, and a folder with no pointers goes straight on to
    // that picker -- so on a big tree the walk can outlast the click's ~5s
    // transient activation, the picker refuses, and the download is refused with
    // it. Without a cache the retry re-runs the identical walk and fails
    // identically: no convergence, and worse than before this pre-flight existed.
    //
    // With it, the second press answers instantly and opens the picker on a
    // fresh gesture. It costs one slow press on a large folder, once.
    //
    // 30s matches the file-list `staleTime` the drive already runs on, and the
    // window is bounded by what it decides: a stale `false` skips a dialog whose
    // options both produce the same archive, and a stale `true` shows one whose
    // "contents" branch is what would have happened anyway.
    const key = fileIds.join(',')
    // #1316 review round 2: a pinned batch's probe (`pinnedBatch`) for an identity no longer current neither reads nor
    // fills the cache — the cache answers for the tab's identity now
    const cacheable = !opts?.identity || isCurrentIdentity(opts.identity)
    const hit = cacheable ? linkedPointerProbeCache.get(key) : undefined
    if (hit && Date.now() - hit.at < 30_000) return hit.value

    const manifestSession = currentSessionSignal()   // #1316: an answer from an ended session is not cached for the next
    const manifestBody = JSON.stringify({ fileIds, manifestOnly: true, anyPointers: true })
    // #1316: bound to the session that sends it — an answer after the end rejects session-ended (and echoes its identity);
    // a pinned batch's step is sent as its identity and finishes (review round 2)
    const res = opts?.identity
      ? await fetchRaw('/api/files/download', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: manifestBody, identity: opts.identity })
      : await sessionFetch(`${getApiBase()}/api/files/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getDefaultHeaders() },
        body: manifestBody,
        credentials: 'include',
      })
    if (!res.ok) throw new Error(`Could not read the folder (${res.status})`)
    const j = (await res.json()) as { pointers?: unknown[] }
    const value = (j.pointers ?? []).length > 0
    if (!manifestSession?.aborted && (!opts?.identity || isCurrentIdentity(opts.identity))) linkedPointerProbeCache.set(key, { at: Date.now(), value })
    return value
  },

  /**
   * Download ONE drive row, routing by whether it is a `.shared`/`.chain`.
   *
   * A pointer must take the POST path: `GET /:id/download` serves the pointer
   * JSON for every pointer by design, so it can never reach the source bytes —
   * a file-target `.shared` saved `index.html.shared` instead of `index.html`.
   * `containsFolder` is the routing flag, not a claim about the target; the
   * server decides the shape (zip for a folder target, the file itself for a
   * file target) and sets Content-Disposition.
   *
   * This lives here rather than at the call sites because there are three of
   * them — the drive, the mobile sheet, and the Shared view — and the first fix
   * only reached one, leaving the other two saving the JSON.
   */
  async downloadItem(
    item: { id: string; name: string; isFolder?: boolean; isPointer?: boolean },
  ): Promise<{ cancelled: boolean }> {
    const isFolder = item.isFolder !== false
    if (item.isPointer) {
      const base = stripPointerSuffix(item.name)
      return filesApi.download([item.id], {
        containsFolder: true,
        suggestedName: isFolder ? `${base}.zip` : base,
      })
    }
    return filesApi.download([item.id], {
      containsFolder: isFolder,
      suggestedName: isFolder ? `${item.name}.zip` : undefined,
    })
  },

  /**
   * Download a pointer's metadata JSON (the `.shared`/`.chain` metadata_json column).
   * Unlike download() — which streams a single file natively via <a href download> and
   * therefore CAN'T observe HTTP errors (a 403/404 silently saves the JSON error body
   * to disk as `download.json`) — metadata is a tiny JSON, so we fetch it through the
   * error-aware path and blob-download. A failed metadata download now THROWS (→ the
   * caller's toast) instead of writing a corrupt file with no signal.
   */
  async downloadMetadata(fileId: string): Promise<void> {
    const tenantId = getActiveTenantId()
    const orgEmail = getActiveOrgEmail()
    const params = new URLSearchParams()
    if (tenantId) params.set('tenantId', tenantId)
    if (orgEmail) params.set('orgEmail', orgEmail)
    const qs = params.toString() ? `?${params.toString()}` : ''
    const oneSent = getDefaultHeaders()
    const oneSentAccount = currentAccount()   // #1316: the account this is sent under
    const res = await fetch(`${getApiBase()}/api/files/${encodeURIComponent(fileId)}/download${qs}`, {
      headers: oneSent,
      credentials: 'include',
    })
    recordActingIdentity(res, oneSent, oneSentAccount)
    if (!res.ok) {
      throw new Error(`Download failed (${res.status})`)
    }
    const filename = parseContentDispositionFilename(res.headers.get('Content-Disposition'), 'metadata.json')
    const blob = await res.blob()
    triggerDownload(blob, filename)
  },

  // ─── Extract ────────────────────────────────────────────────────────

  /**
   * Preview zip contents (list filenames without extracting)
   */
  async previewZip(fileId: string): Promise<{ files: string[] }> {
    return request(`/api/files/${fileId}/extract-preview`, {
      method: 'POST',
    })
  },

  /**
   * Pre-flight per-file conflict list for the merge action.
   *
   * When the user picks "Replace" (Merge for a folder) at the top-level
   * conflict dialog, the extract would silently overwrite any number of
   * files inside the merged folder. Calling this first surfaces the
   * exact file collisions so the SPA can render a per-file resolution
   * sheet — the user picks which paths to skip, and those paths are
   * passed back to extractZip() as `skipPaths`.
   */
  async previewExtractConflicts(
    fileId: string,
    destinationParentId?: string | null,
  ): Promise<{
    conflicts: Array<{
      zipPath: string
      destPath: string
      existingFileId: string
      existingIsFolder: boolean
    }>
  }> {
    return request('/api/files/extract/preview-conflicts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileId, destinationParentId }),
    })
  },

  /**
   * Extract a zip file. `perPathResolutions` lets the caller pass back
   * a per-zip-entry strategy override map (collected via per-file
   * ConflictModal prompts) — values are 'skip' / 'keepBoth' / 'replace'. Entries not in the map use the global `conflictStrategy`.
   */
  async extractZip(
    fileId: string,
    destinationParentId?: string | null,
    conflictStrategy?: ConflictStrategy,
    operationId?: string,
    perPathResolutions?: Record<string, ConflictStrategy>,
  ): Promise<{
    fileIds: string[]
    /** #1430: what the undo bins — the created items whose parent the extract did not create (an older server: absent). */
    topFileIds?: string[]
    /** Existing files the extract's Replace wrote a version into, + the version replaced (undo). */
    versioned?: Array<{ fileId: string; previousVersionId: string; versionId?: string }>
    filesExtracted: number
    bytesExtracted: number
    destinationFolderId: string | null
    /** Owner D145 — entries skipped because a name on their path is reserved in the drive. */
    skipped?: Array<{ path: string; error: string; code: string }>
  }> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (operationId) headers['x-operation-id'] = operationId
    // #762: keyed, and asked once more on a lost answer — the resend joins the queued (large-archive) job, or gets
    // a small archive's stored answer back; it is never extracted twice.
    headers[IDEMPOTENCY_KEY_HEADER] = mintIdempotencyKey('extract')
    // #282: a LARGE archive (server-side threshold / EXTRACT_VIA_WORKER) now
    // returns 202 `{ async: true, jobId }` and runs in the worker; a small one
    // still returns the terminal 200 body inline. Same shape as POST /copy →
    // pollCopyJob. The generous client timeout still covers the inline path.
    const res = await sendOnceMore(() => request<
      | { async?: false; fileIds: string[]; topFileIds?: string[]; versioned?: Array<{ fileId: string; previousVersionId: string; versionId?: string }>; filesExtracted: number; bytesExtracted: number; destinationFolderId: string | null; skipped?: Array<{ path: string; error: string; code: string }> }
      | { async: true; jobId: string; destinationFolderId: string | null }
    >('/api/files/extract', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        fileId, destinationParentId, conflictStrategy: toWireConflictStrategy(conflictStrategy),
        perPathResolutions: perPathResolutions && Object.fromEntries(Object.entries(perPathResolutions).map(([k, v]) => [k, toWireConflictStrategy(v)])),
      }),
      // Synchronous (small-archive) extraction legitimately runs for a while
      // (94s observed for a 4.5k-entry zip), so the 30s default request timeout
      // aborts the client while the server keeps going. Own the lifecycle with
      // a generous ceiling. (Large archives return 202 immediately and we poll.)
      signal: AbortSignal.timeout(10 * 60_000),
    }))
    if ('async' in res && res.async) {
      return pollExtractJob(res.jobId, res.destinationFolderId)
    }
    return res
  },

  // ─── Quarantine Operations ─────────────────────────────────────────

  async listQuarantine(): Promise<QuarantineItem[]> {
    const res = await request<{ items: QuarantineItem[] }>('/api/quarantine')
    return res.items
  },

  async getQuarantineCount(): Promise<number> {
    const res = await request<{ count: number }>('/api/quarantine/count')
    return res.count
  },

  async deleteQuarantineItem(itemId: string): Promise<void> {
    await request(`/api/quarantine/${itemId}`, { method: 'DELETE' })
  },

  async downloadQuarantineItem(itemId: string): Promise<void> {
    const res = await fetchRaw(`/api/quarantine/${itemId}/download`, {
      headers: getDefaultHeaders(),
    })

    if (!res.ok) {
      throw new Error('Download failed')
    }

    const filename = parseContentDispositionFilename(res.headers.get('Content-Disposition'), itemId)
    const blob = await res.blob()
    triggerDownload(blob, filename)
  },

  async restoreQuarantineItem(itemId: string): Promise<{ fileId: string; restoredName: string }> {
    return request(`/api/quarantine/${itemId}/restore`, { method: 'POST' })
  },

  async triggerRescan(days = 30): Promise<{
    scanned: number
    clean: number
    infected: number
    errors: number
    quarantined: string[]
    duration: number
  }> {
    return request('/api/quarantine/rescan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ days }),
    })
  },
}

// ─── PDApp app-level versions (#612) ──────────────────────────────────

export const appVersionsApi = {
  /** `restorable: false` — an app reached through a route into another org: restoring it is that org's (#1534 I2). */
  async list(pdappId: string): Promise<{ appVersions: AppVersion[]; restorable?: boolean }> {
    return request<{ appVersions: AppVersion[]; restorable?: boolean }>(`/api/files/${pdappId}/app-versions`)
  },
  async restore(pdappId: string, appVersionId: string): Promise<{ restored: number; recreated: number; binned: number }> {
    return request(`/api/files/${pdappId}/app-versions/${appVersionId}/restore`, { method: 'POST' })
  },

  // ─── App-version retention (per-pdapp override, #612 follow-up) ───────

  /**
   * Read this PDApp's effective app-version retention: any per-pdapp
   * override, the resolved count actually in force (the ladder: the
   * override -> the tenant's app-ceiling -> the shipped default of 50),
   * and which layer won.
   */
  /** `editable: false` — through a route into another org the setting is shown, never changed there (#1534 I2). */
  async getRetention(pdappId: string): Promise<{ override: number | null; resolved: number; source: 'pdapp' | 'tenant' | 'shipped'; editable?: boolean }> {
    return request<{ override: number | null; resolved: number; source: 'pdapp' | 'tenant' | 'shipped'; editable?: boolean }>(`/api/files/${pdappId}/app-version-retention`)
  },

  /**
   * Set a per-pdapp app-version-retention override (max retained app
   * versions, 1-500).
   */
  async setRetention(pdappId: string, maxVersions: number, opts?: { purge?: boolean }): Promise<{ ok: true } & RetentionPurgeResponse> {
    return request<{ ok: true } & RetentionPurgeResponse>(`/api/files/${pdappId}/app-version-retention`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ maxVersions, purge: opts?.purge === true }),
    })
  },

  /** What setting (`maxVersions`) or clearing (`null`) this app's own app-version limit would delete (#1443). */
  async retentionPurgePreview(pdappId: string, maxVersions: number | null): Promise<RetentionPurgePreviewResponse> {
    return request<RetentionPurgePreviewResponse>(`/api/files/${pdappId}/app-version-retention/purge-preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ maxVersions }),
    })
  },

  /**
   * Clear a per-pdapp app-version-retention override — falls back to the
   * fixed default.
   */
  async clearRetention(pdappId: string, opts?: { purge?: boolean }): Promise<{ ok: true } & RetentionPurgeResponse> {
    return request<{ ok: true } & RetentionPurgeResponse>(`/api/files/${pdappId}/app-version-retention${opts?.purge ? '?purge=1' : ''}`, { method: 'DELETE' })
  },
}

/** What a retention-lowering Save would delete — the purge's own plan (#1443). The dialog type is
 *  `RetentionPurgePreviewView` (@papan/core-ui). */
export interface RetentionPurgePreviewResponse {
  willPurge: boolean
  files: number
  versions: number
  bytes: number
  totalBytes: number
  totalVersions: number
  appVersions: number
  exact: boolean
  truncated: boolean
  samples: { fileId: string; relPath: string; before: number; after: number; bytes: number; totalBytes: number; rule: string | null; folderRule?: string; builtIn?: boolean }[]
  byRule: { rule: string | null; folderRule?: string; builtIn?: boolean; files: number; versions: number; bytes: number }[]
}

/** What a Save that purged reports (#1443). */
export interface RetentionPurgeResponse {
  purged?: { files: number; versions: number; appVersions?: number }
  queued?: { jobId: string }
}

/** D18: a row that narrowed an app's grant to its granter's own access (below the granted folder). */
export interface MiniAppGrantMirror { folderId: string; permission: string }

export interface MiniAppGrant {
  grantId: string
  folderId: string
  folderName: string
  permission: string
  /** Whether the app principal may re-share this folder onward. App principals
   *  default to false. */
  allowSharing: boolean
  /** True when this grant is on the app's OWN `.miniapp` folder. */
  isSelf: boolean
  /** True when the grant folder is the app folder OR a descendant of it (e.g. the
   *  protected `_appsystem/`). Internal grants live inside the app folder already, so
   *  they never need a `.chain` — only external grants do. */
  isInternal: boolean
  /** D18: set when this row is a LIMIT — the app narrowed, below grant `grantId`, to the access
   *  of the person who set that grant (`setBy`, their org email). Not a grant of its own. */
  limitOf?: { grantId: string; setBy: string; /** the viewer can open the folder — may set the app's level there (it becomes a real grant) */ editable?: boolean }
  /** D20: the grant id this one is grouped under (on an ancestor folder; removed with it). */
  groupedUnder?: string
  /** A chain brings in this folder or an ancestor, or it is inside the app folder — no chain warning. */
  coveredByChain?: boolean
  /** #1235 — the app's own level on the `.chain`(s) bringing this folder in (the best of them): through a chain the
   *  app holds min(this grant, that level) — placement is the only cap. Absent when no chain reaches it. */
  chainPlacement?: string
  /** D20: grant ids of the app's grants on ANCESTOR folders — where this one may be grouped. */
  groupCandidates?: string[]
  /** The most this viewer may set on this folder (their own access; a shell reads as read-only). */
  viewerMax?: string
  /** The viewer's own sharing right on the folder — "Allow sharing" is offered only then. */
  viewerCanShare?: boolean
}

/** A `.chain` file inside the `.miniapp` folder (a pointer to a folder brought
 *  into the app's working context). */
export interface MiniAppChain {
  chainId: string
  name: string
  targetFileId: string | null
  targetName: string | null
}

/**
 * MiniApp app-principal access management — the MiniApp editor's Principal Access
 * section. Grants/revokes the app principal's folder access and auto-manages the
 * `.chain` pointer inside the `.miniapp` folder so the granted folder appears in
 * the app's working context. Server: features/files/api/miniapp-access.ts.
 * The grant is LIVE: the app principal resolves it (`resolveAppPrincipalPermission`, #367).
 */
/** What `miniappApi.getMetadata` hands back — the shape an app embeds and
 *  `papan.pdappIconByMetadata()` draws (#788). `icon` carries the manifest spec
 *  plus the two parts a manifest cannot carry across: inlined `assets` and a
 *  Lucide `glyph`'s geometry. */
/** What the export hands back — the manifest subset #788 defines. Declared ONCE,
 *  in `@papan/types`, because the renderer, the markup builder and this client
 *  all have to agree about it; re-exported here so callers of `miniappApi` have
 *  it to hand. */
export type { PortablePdappMetadata } from '@papan/types/miniapp-icon-svg'
import type { PortablePdappMetadata } from '@papan/types/miniapp-icon-svg'

export const miniappApi = {
  /** Load the app's access list + the chains in its folder. Server-gated on the
   *  Modification Safeguard (write on the .miniapp folder AND user.access ≥ app.access
   *  on every granted folder) — a 403 means the caller can't manage this miniapp. */
  async listAccess(miniappFolderId: string): Promise<{ access: MiniAppGrant[]; chains: MiniAppChain[]; canManageApp?: boolean; deepChainsIncomplete?: boolean }> {
    return request(`/api/miniapp/${miniappFolderId}/access`)
  },

  /** #788 — another PDApp's PORTABLE metadata (name + icon, assets inlined), for
   *  an author embedding a link to it. Gated on THIS user's read of the target;
   *  a target they cannot read answers 404, the same as one that does not exist.
   *  The result is a COPY: it does not follow the target's later changes. */
  async getMetadata(miniappFolderId: string): Promise<PortablePdappMetadata> {
    return request(`/api/miniapp/${miniappFolderId}/metadata`)
  },

  /** Read-only install-state — any viewer with READ on the .miniapp folder. Lets
   *  the runner show a "needs install" message to a read-only audience instead of
   *  running an uninstalled app degraded. Returns only the boolean. */
  async isInstalled(miniappFolderId: string): Promise<{ installed: boolean }> {
    return request(`/api/miniapp/${miniappFolderId}/installed`)
  },

  /** Install the app: establish the app principal (readwrite self-grant on the
   *  `.miniapp` folder) + create `_appsystem/` with a readonly grant. The consent
   *  point — an app doesn't run until installed. Idempotent. */
  async install(miniappFolderId: string): Promise<{ ok: boolean; installed: boolean; mirrored?: MiniAppGrantMirror[] }> {
    return request(`/api/miniapp/${miniappFolderId}/install`, { method: 'POST' })
  },

  /** Grant the app access to `targetFolderId`, then auto-chain it into the app folder
   *  (except a SELF grant — the app's own folder needs no chain into itself). Pass
   *  `skipChain` when a `.chain` to this target ALREADY exists (e.g. granting access
   *  to an orphan chain) so we don't create a duplicate — dedup is the caller's job
   *  since only it holds the current chain list (targetFileId isn't on FileListItem). */
  async grantAccess(miniappFolderId: string, targetFolderId: string, permission: string, opts?: { skipChain?: boolean }): Promise<MiniAppGrant & { mirrored?: MiniAppGrantMirror[] }> {
    const { grant, mirrored } = await request<{ ok: boolean; grant: MiniAppGrant; mirrored?: MiniAppGrantMirror[] }>(`/api/miniapp/${miniappFolderId}/access`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetFolderId, permission }),
    })
    if (targetFolderId !== miniappFolderId && !opts?.skipChain) {
      // Auto-chain via the tested POST /api/files/chain. Best-effort: the grant is
      // authoritative; a failed chain just means the folder isn't in the app's
      // context yet (surfaced as a "no chain" warning; re-grant / add-chain re-attempts).
      try {
        await filesApi.createChain(miniappFolderId, targetFolderId)
      } catch (err) {
        console.warn('[miniapp] auto-chain failed (grant persisted):', err)
      }
    }
    return { ...grant, ...(mirrored ? { mirrored } : {}) }
  },

  /** Dry run of removing the app's access on a folder (the real removal, rolled back): who would no
   *  longer be able to edit the app. Throws exactly as the removal would when it's refused. */
  async previewRevoke(miniappFolderId: string, targetFolderId: string): Promise<{ losesEditAccess: Array<{ name: string | null; email: string; isYou: boolean }> }> {
    return request(`/api/miniapp/${miniappFolderId}/access/${targetFolderId}?dryRun=1`, { method: 'DELETE' })
  },

  /** Change the permission on an EXISTING grant. Unlike grantAccess, this does NOT
   *  touch the chain (the folder is already in the app's context) — the server's
   *  POST upserts the folder_permissions row by (folder, app principal). The
   *  re-sharing flag is preserved (only `permission` is sent). */
  async setPermission(miniappFolderId: string, targetFolderId: string, permission: string): Promise<MiniAppGrant & { mirrored?: MiniAppGrantMirror[] }> {
    const { grant, mirrored } = await request<{ ok: boolean; grant: MiniAppGrant; mirrored?: MiniAppGrantMirror[] }>(`/api/miniapp/${miniappFolderId}/access`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetFolderId, permission }),
    })
    return { ...grant, ...(mirrored ? { mirrored } : {}) }
  },

  /** Toggle the re-sharing flag on an EXISTING grant (permission preserved). */
  async setSharing(miniappFolderId: string, targetFolderId: string, allowSharing: boolean): Promise<MiniAppGrant> {
    const { grant } = await request<{ ok: boolean; grant: MiniAppGrant }>(`/api/miniapp/${miniappFolderId}/access`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetFolderId, allowSharing }),
    })
    return grant
  },

  /** D20: group the grant on `targetFolderId` under the app's grant on the ANCESTOR folder
   *  `underFolderId` (removing that one later removes this too); `null` ungroups. */
  async groupGrant(miniappFolderId: string, targetFolderId: string, underFolderId: string | null): Promise<void> {
    await request(`/api/miniapp/${miniappFolderId}/access/${targetFolderId}/group`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ underFolderId }),
    })
  },

  /** Revoke the grant, and (unless `keepChain`) remove its `.chain`. The chain is
   *  matched SERVER-SIDE by targetFileId (rename-proof — a user can rename the `.chain`
   *  file, which changes only its name, never targetFileId), and binned here via the
   *  tested deleteFile. `keepChain` is the "No Access" case: drop the app's authority
   *  but leave the folder chained (it becomes an orphan chain, surfaced with a warning).
   *  Full removal (grant + chain) is the default — the dropdown's "Remove access". */
  async revokeAccess(miniappFolderId: string, targetFolderId: string, opts?: { keepChain?: boolean }): Promise<void> {
    const { chainId } = await request<{ ok: boolean; chainId: string | null }>(
      `/api/miniapp/${miniappFolderId}/access/${targetFolderId}`, { method: 'DELETE' },
    )
    if (chainId && !opts?.keepChain) {
      try {
        // Inline, not the delete job: a `.chain` is one pointer file, never a big tree (#921).
        await filesApi.deleteFile(chainId)
      } catch (err) {
        console.warn('[miniapp] chain cleanup on revoke failed (grant revoked):', err)
      }
    }
  },

  /** Load the app's triggers from `_appsystem/triggers.json` (WRITE-gated). Returns
   *  the pause-all flag, the trigger list, and per-trigger fire counts (keyed by
   *  trigger id). Empty on an absent/invalid file. */
  async getTriggers(miniappFolderId: string): Promise<{ paused: boolean; triggers: Trigger[]; schedules: Schedule[]; schedulesPaused: boolean; counts: Record<string, { count: number; since: number }>; scheduleConsent?: Record<string, 'allowed' | 'paused' | 'denied' | 'pending' | 'invalid'>; problems?: string[] }> {
    return request(`/api/miniapp/${miniappFolderId}/triggers`)
  },

  /** Persist the FULL triggers file to `_appsystem/triggers.json` (server validates
   *  against TriggersFileSchema; 400 on a malformed filter/action). This is a
   *  whole-file replace, so ALWAYS pass the complete current state — omitting
   *  `schedules` would wipe them (and vice versa). `paused` pauses event-triggers;
   *  `schedulesPaused` pauses schedules — they are independent. */
  async saveTriggers(
    miniappFolderId: string,
    file: { triggers: Trigger[]; paused?: boolean; schedules?: Schedule[]; schedulesPaused?: boolean },
  ): Promise<{ ok: boolean; triggers: Trigger[] }> {
    return request(`/api/miniapp/${miniappFolderId}/triggers`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        paused: file.paused ?? false,
        triggers: file.triggers,
        schedules: file.schedules ?? [],
        schedulesPaused: file.schedulesPaused ?? false,
      }),
    })
  },

  // ─── Per-item consent (install wizard) ──────────────────────────────

  /** Declared capabilities with no human decision yet (the wizard's payload),
   *  plus the current decision rows. */
  async getConsentOutstanding(miniappFolderId: string): Promise<{
    outstanding: MiniAppConsentItem[]
    decisions: MiniAppConsentDecision[]
  }> {
    return request(`/api/miniapp/${miniappFolderId}/consent/outstanding`)
  },

  /** Record an allow/deny decision on an item (by content hash). A grant allow needs
   *  `targetFolderId` so the server can create the folder_permissions authority. */
  async decideConsent(miniappFolderId: string, item: { itemType: 'trigger' | 'grant' | 'schedule' | 'key' | 'key-pick'; contentHash: string; decision: 'allowed' | 'denied'; label?: string; targetFolderId?: string }, opts?: PinnedIdentityOptions): Promise<{ ok: boolean }> {
    return request(`/api/miniapp/${miniappFolderId}/consent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item),
      ...pinnedOpt(opts),
    })
  },

  /** Decide MANY items in one request — what the permission flow's Confirm sends.
   *  (Chain-placement rules live in `./chain-rules.js`, re-exported below.)
   *  Each item runs through the SAME gate as the single route; the server publishes
   *  ONE permission event for the batch (the event drops the running app's socket,
   *  so one confirm is one reconnect instead of N). Partial success is normal and
   *  not an error: read `results` per item. */
  async decideConsentBatch(
    miniappFolderId: string,
    decisions: Array<{ itemType: 'trigger' | 'grant' | 'schedule' | 'key' | 'key-pick'; contentHash: string; decision: 'allowed' | 'denied'; label?: string; targetFolderId?: string }>,
    /** #1316 review round 2: the person's Confirm (`pinnedBatch().opts()`) — every chunk is sent as who confirmed. */
    opts?: PinnedIdentityOptions,
  ): Promise<{ ok: boolean; applied: number; results: Array<{ contentHash: string | null; ok: boolean; error?: string }> }> {
    // The route caps a batch at 100; chunk here so an app with more outstanding
    // items than that stays answerable (the old per-item loop had no cap, and a
    // 400 would have made the flow unfinishable).
    const CHUNK = 100
    const out: { ok: boolean; applied: number; results: Array<{ contentHash: string | null; ok: boolean; error?: string }> } = { ok: true, applied: 0, results: [] }
    try {
      for (let i = 0; i < decisions.length; i += CHUNK) {
        const res = await request<{ ok: boolean; applied: number; results: Array<{ contentHash: string | null; ok: boolean; error?: string }> }>(
          `/api/miniapp/${miniappFolderId}/consent/batch`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ decisions: decisions.slice(i, i + CHUNK) }), ...pinnedOpt(opts) },
        )
        out.ok = out.ok && res.ok
        out.applied += res.applied
        out.results.push(...res.results)
      }
      return out
    } catch (err) {
      // VERSION SKEW: the SPA and the API deploy independently, so during a
      // rolling restart an old instance has no batch route and answers 404.
      // Without this every PDApp is unapprovable for the length of the deploy.
      // The per-item route is the pre-batch behaviour — N events instead of one,
      // which is the right trade for a few minutes. `decide` upserts by content
      // hash, so replaying a decision already applied by a successful chunk is
      // a no-op.
      if ((err as { status?: number } | null)?.status !== 404) throw err
      // A 404 also means "this app folder is gone / not a PDApp" — the route's
      // own refusal. Retrying 100 decisions one by one against that would just
      // 404 a hundred times AND report partial success to a caller that should
      // have seen a hard failure. Ask an endpoint that exists in both versions
      // which 404 this is.
      await miniappApi.isInstalled(miniappFolderId).catch(() => { throw err })
      const results: Array<{ contentHash: string | null; ok: boolean; error?: string }> = []
      let applied = 0
      for (const d of decisions) {
        try {
          await miniappApi.decideConsent(miniappFolderId, d, opts)
          applied += 1
          results.push({ contentHash: d.contentHash, ok: true })
        } catch (e) {
          results.push({ contentHash: d.contentHash, ok: false, error: e instanceof Error ? e.message : String(e) })
        }
      }
      return { ok: results.every(r => r.ok), applied, results }
    }
  },

  /** Pause / resume an allowed trigger (operational, not a consent change). */
  async pauseConsent(miniappFolderId: string, contentHash: string, paused: boolean): Promise<{ ok: boolean }> {
    return request(`/api/miniapp/${miniappFolderId}/consent/pause`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contentHash, paused }),
    })
  },

  /** Load the mini-app's custom icon spec (stored under the `miniappIcon` key of
   *  the .miniapp folder's metadata_json). `null` when none is set — the caller
   *  falls back to the derived default (`defaultIconSpecFor`). READ-visibility
   *  gated (weaker than manage): anyone who can see the folder can read it. */
  async getIcon(miniappFolderId: string): Promise<{ icon: MiniappIconSpec | null; rev: string | null }> {
    return request(`/api/miniapp/${miniappFolderId}/icon`)
  },

  /** Persist (or reset) the mini-app's custom icon. Pass the spec to set it, or
   *  `null` to reset to the derived default (removes the key). Server validates
   *  against MiniappIconSpecSchema, MERGE-writes metadata_json, and publishes a
   *  realtime `file:updated` so open Drive lists update the tile live. Manage-
   *  gated (write on the .miniapp folder + the modification safeguard). */
  async setIcon(miniappFolderId: string, icon: MiniappIconSpec | null): Promise<{ icon: MiniappIconSpec | null }> {
    return request(`/api/miniapp/${miniappFolderId}/icon`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ icon }),
    })
  },

  /** Upload a custom icon ASSET (kind 'bg' = background PNG/JPEG, 'fg' = foreground
   *  SVG/PNG). The server malware-scans + validates/normalizes it (an SVG is
   *  hardened + rasterized to PNG). Call BEFORE `setIcon` with the matching spec
   *  flag (bg.type='image' / image:true). Manage-gated. Returns the new rev. */
  async setIconAsset(miniappFolderId: string, kind: 'bg' | 'fg', file: Blob): Promise<{ ok: boolean; kind: string; hash: string; sourceFormat: string }> {
    const res = await fetchRaw(`/api/miniapp/${miniappFolderId}/icon-asset/${kind}`, {
      method: 'POST',
      body: file,
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error((body as { error?: string }).error || 'Icon asset upload failed')
    }
    return res.json()
  },

  /** Read back ONE stored icon asset (bg = JPEG, fg = PNG). Manage-gated. `null`
   *  when the slot has no asset (404) — the editor treats that as "nothing to
   *  preview" and falls back to the spec's own background. Used by the editor to
   *  composite the DRAFT over already-SAVED assets; `icon.png` can't serve that
   *  because it bakes in the saved foreground. */
  async getIconAsset(miniappFolderId: string, kind: 'bg' | 'fg'): Promise<Blob | null> {
    const res = await fetchRaw(`/api/miniapp/${miniappFolderId}/icon-asset/${kind}`)
    if (res.status === 404) return null
    if (!res.ok) throw new Error('Icon asset fetch failed')
    return res.blob()
  },

  /** Read the running app's auto-refresh preference (reload the preview when a
   *  source file changes). Defaults to `true`. Read-visibility gated. */
  async getAutoRefresh(miniappFolderId: string): Promise<{ autoRefresh: boolean }> {
    return request(`/api/miniapp/${miniappFolderId}/auto-refresh`)
  },

  /** Persist the auto-refresh preference. MERGE-writes metadata_json + publishes
   *  a realtime `file:updated`. Manage-gated (write + modification safeguard). */
  async setAutoRefresh(miniappFolderId: string, autoRefresh: boolean): Promise<{ autoRefresh: boolean }> {
    return request(`/api/miniapp/${miniappFolderId}/auto-refresh`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ autoRefresh }),
    })
  },
}

/** `key-pick`: one of the app's secrets this person must CHOOSE a key for (never allowed as a consent — chosen through
 *  the app's Variables and secrets, or set aside with `denied`). */
export interface MiniAppConsentItem { itemType: 'trigger' | 'grant' | 'schedule' | 'key' | 'key-pick'; contentHash: string; label: string; targetFolderId?: string; schedule?: { cron?: string; tz?: string; everySeconds?: number }; key?: { entryId: string; names: string[]; label: string | null; canAllow: boolean }; pick?: { name: string; description: string | null; needs: 'no-key' | 'unavailable' | 'no-field' } }
export interface MiniAppConsentDecision { itemType: 'trigger' | 'grant' | 'schedule' | 'key' | 'key-pick'; contentHash: string; decision: 'allowed' | 'denied'; paused: boolean; label: string | null; decidedBy: string | null }

export type { Trigger, Schedule } from '@papan/types'

export { debugLogApi, debugLogTargetFor, type DebugLogEntry, type DebugLogKind } from './debug-log.js'
export { appRunLogApi, type AppRunEvent, type AppRunEventKind, type AppRunLogResult, type AppRunWriteSource, type AppRunWritesResult } from './app-run-log.js'
export { chainRedundancy, type ChainRedundancy, type ChainRedundancyInput } from './chain-rules.js'
export { createEmptyBinTracker, runEmptyBinJob, type EmptyBinTracker, type EmptyBinTrackerDeps, type EmptyBinJobOutcome, type EmptyBinRunDeps, type EmptyBinRunHooks } from './empty-bin-tracker.js'
export { createBinPurgeTracker, runBinPurgeJob, type BinPurgeTracker, type BinPurgeTrackerDeps, type BinPurgeJobOutcome, type BinPurgeStartResult, type BinPurgeJobStatus, type BinPurgeRunDeps, type BinPurgeRunHooks } from './bin-purge-tracker.js'
export { createRestoreTracker, pendingRestores, inFlightRestores, type RestoreTracker, type RestoreTrackerDeps, type RestoreJobOutcome, type RestoreJobProgress, type PendingRestore } from './restore-tracker.js'
export { createDeleteTracker, pendingDeletes, inFlightDeletes, type DeleteTracker, type DeleteTrackerDeps, type DeleteJobOutcome, type DeleteJobProgress, type PendingDelete } from './delete-tracker.js'
export { createJobTracker, createInFlightSet, createPendingJobs, requestOutcomeUnknown, requestSignal, isDbVerifyingError, JobFailedError, JobLostError, JOB_DEADLINE_MS, type JobKind, type JobStatus, type JobProgress, type JobTracker, type JobTrackerDeps, type JobTrackerTimings, type InFlightSet, type PendingJob, type PendingJobs } from './job-tracker.js'
